import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Helmet } from 'react-helmet-async';
import axios from 'axios';
import { useAuth } from '@/context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Skeleton } from '@/components/ui/skeleton';
import { Switch } from '@/components/ui/switch';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Input } from '@/components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { 
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Users, BarChart2, FileText, Trash2, Eye, Shield, CreditCard, TrendingUp, PoundSterling, Settings, AlertTriangle, ArrowUp, Plus, Key, Ban, MoreVertical, RefreshCw, Mail, Send, MessageSquare, Lock, Unlock, Link2, CheckSquare, Square, Copy, XCircle, ChevronDown, Search, Check, Clock, Globe, Monitor } from 'lucide-react';
import { toast } from 'sonner';
import { AppLayout } from '@/components/AppLayout';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function AdminPage() {
  const { user, isAdmin, loading: authLoading } = useAuth();
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [payments, setPayments] = useState(null);
  const [subscriptions, setSubscriptions] = useState(null);
  const [settings, setSettings] = useState({ payments_enabled: true, maintenance_mode: false });
  const [dataLoading, setDataLoading] = useState(true);
  const [selectedUser, setSelectedUser] = useState(null);
  const [upgradeUser, setUpgradeUser] = useState(null);
  const [upgradeTier, setUpgradeTier] = useState('core');
  const [activeTab, setActiveTab] = useState('overview');
  
  // New state for audits tab
  const [allAudits, setAllAudits] = useState([]);
  const [auditsLoading, setAuditsLoading] = useState(false);
  const [auditsTotal, setAuditsTotal] = useState(0);
  const [auditsPage, setAuditsPage] = useState(0);
  // Default to today's date for both from and to
  const getTodayDate = () => new Date().toISOString().split('T')[0];
  const [auditsFilter, setAuditsFilter] = useState({ search: '', dateFrom: getTodayDate(), dateTo: getTodayDate(), status: '' });
  const [selectedAuditIds, setSelectedAuditIds] = useState([]);
  const [deletingAudits, setDeletingAudits] = useState(false);
  
  // Users search filter
  const [usersSearch, setUsersSearch] = useState('');
  
  // New state for messages tab
  const [messages, setMessages] = useState([]);
  const [messagesLoading, setMessagesLoading] = useState(false);
  const [replyingTo, setReplyingTo] = useState(null);
  const [replyText, setReplyText] = useState('');
  const [expandedMessageId, setExpandedMessageId] = useState(null);
  const [unreadContactCount, setUnreadContactCount] = useState(0);
  
  // Inbound emails state
  const [inboundEmails, setInboundEmails] = useState([]);
  const [expandedInboundId, setExpandedInboundId] = useState(null);
  const [unreadInboundCount, setUnreadInboundCount] = useState(0);
  
  // New state for bulk email
  const [bulkEmail, setBulkEmail] = useState({ subject: '', body: '', cta_text: '', cta_url: '', audience: 'all', track_clicks: false });
  const [sendingBulk, setSendingBulk] = useState(false);
  const [emailCampaigns, setEmailCampaigns] = useState([]);
  const [expandedCampaignId, setExpandedCampaignId] = useState(null);
  
  // Recipients state for bulk email
  const [previewRecipients, setPreviewRecipients] = useState([]);
  const [selectedRecipients, setSelectedRecipients] = useState([]);
  const [customEmail, setCustomEmail] = useState('');
  const [loadingRecipients, setLoadingRecipients] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState(null);
  
  // Prefill state for Send Offer
  const [emailPrefill, setEmailPrefill] = useState(null);
  
  // Autocomplete state for user search
  const [userSuggestions, setUserSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  // Click tracking details modal
  const [clickDetailsAudit, setClickDetailsAudit] = useState(null);
  
  // Delete audit confirmation
  const [deleteAuditTarget, setDeleteAuditTarget] = useState(null);
  const [deletingAudit, setDeletingAudit] = useState(false);
  
  // Bulk delete confirmation
  const [bulkDeleteOpen, setBulkDeleteOpen] = useState(false);
  const [filterDeleteOpen, setFilterDeleteOpen] = useState(false);
  
  // New state for admin actions
  const [addCreditsUser, setAddCreditsUser] = useState(null);
  const [creditsToAdd, setCreditsToAdd] = useState(1);
  const [resetPasswordUser, setResetPasswordUser] = useState(null);
  const [newPassword, setNewPassword] = useState('');
  const [blockUser, setBlockUser] = useState(null);
  const [blockReason, setBlockReason] = useState('');
  const [stripeCustomerUser, setStripeCustomerUser] = useState(null);
  const [stripeCustomerId, setStripeCustomerId] = useState('');
  
  // Popup announcement config
  const [popupConfig, setPopupConfig] = useState({ enabled: false, title: '', text: '', bg_color: '#1a1a2e', cta_text: '', cta_link: '' });
  const [savingPopup, setSavingPopup] = useState(false);
  
  // Finance state
  const [finance, setFinance] = useState(null);
  const [financeLoading, setFinanceLoading] = useState(false);
  const [editingCost, setEditingCost] = useState(false);
  const [costInput, setCostInput] = useState('');
  const [savingCost, setSavingCost] = useState(false);
  
  // Pricing state - with proper default structure
  const defaultPricing = {
    id: 'global',
    tiers: {
      core: { 
        name: 'Core', 
        price: 29, 
        currency: 'GBP', 
        features: [
          '4 credits/month',
          'Full Depth scan (unlimited pages)',
          '1 monitored domain',
          'Full report + Actions',
          'PDF export',
          'Email support (24-48h)'
        ], 
        discount_percent: 0, 
        special_offer: false, 
        special_offer_text: '',
        description: 'For solo founders & small businesses'
      },
      pro: { 
        name: 'Pro', 
        price: 79, 
        currency: 'GBP', 
        features: [
          '12 credits/month',
          'Full Depth scan (unlimited pages)',
          'Up to 3 monitored domains',
          '3 competitors per audit',
          'Full report + Actions',
          'PDF export',
          'Competitor analysis',
          'Priority support (12h)'
        ], 
        discount_percent: 0, 
        special_offer: false, 
        special_offer_text: '',
        description: 'Comprehensive analysis with competitors'
      },
      enterprise: { 
        name: 'Enterprise', 
        description: 'For agencies & developers with custom requirements', 
        features: [
          'Unlimited audits',
          'Unlimited domains',
          'Custom credit allocation',
          'White-label options',
          'Priority support',
          'Custom integrations',
          'Dedicated account manager'
        ] 
      }
    }
  };
  const [pricing, setPricing] = useState(defaultPricing);
  const [savingPricing, setSavingPricing] = useState(false);
  
  // Discount codes state
  const [discountCodes, setDiscountCodes] = useState([]);
  const [newCode, setNewCode] = useState({
    code: '',
    discount_type: 'percent',
    discount_value: '',
    max_uses: '',
    expires_at: '',
    valid_for_tiers: ['core', 'pro'],
    description: '',
    new_users_only: false
  });
  const [creatingCode, setCreatingCode] = useState(false);

  useEffect(() => {
    if (authLoading) return;
    if (isAdmin) {
      fetchData();
    } else {
      setDataLoading(false);
    }
  }, [isAdmin, authLoading]);

  const fetchData = async () => {
    try {
      const [statsRes, usersRes, paymentsRes, subsRes, settingsRes, pricingRes, codesRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/admin/users`),
        axios.get(`${API}/admin/payments`),
        axios.get(`${API}/admin/subscriptions`),
        axios.get(`${API}/admin/settings`).catch(() => ({ data: { payments_enabled: true, maintenance_mode: false } })),
        axios.get(`${API}/admin/pricing`).catch(() => ({ data: null })),
        axios.get(`${API}/admin/discount-codes`).catch(() => ({ data: { codes: [] } }))
      ]);
      setStats(statsRes.data);
      setUsers(usersRes.data.users);
      setPayments(paymentsRes.data);
      setSubscriptions(subsRes.data);
      setSettings(settingsRes.data);
      // Merge pricing with defaults to ensure all fields exist
      if (pricingRes.data && pricingRes.data.tiers) {
        const dbTiers = pricingRes.data.tiers;
        
        // Helper to get features - use DB if non-empty, otherwise defaults
        const getFeatures = (dbFeatures, defaultFeatures) => {
          if (dbFeatures && dbFeatures.length > 0) return dbFeatures;
          return defaultFeatures;
        };
        
        setPricing(prev => ({
          ...prev,
          ...pricingRes.data,
          tiers: {
            // Handle 'entry' from DB as 'core' (backward compatibility)
            core: { 
              ...prev.tiers.core, 
              ...(dbTiers.core || dbTiers.entry || {}),
              features: getFeatures(dbTiers.core?.features || dbTiers.entry?.features, prev.tiers.core.features)
            },
            pro: { 
              ...prev.tiers.pro, 
              ...(dbTiers.pro || {}),
              features: getFeatures(dbTiers.pro?.features, prev.tiers.pro.features)
            },
            enterprise: { 
              ...prev.tiers.enterprise, 
              ...(dbTiers.enterprise || {}),
              features: getFeatures(dbTiers.enterprise?.features, prev.tiers.enterprise.features)
            }
          }
        }));
      }
      setDiscountCodes(codesRes.data.codes || []);
      // Fetch unread inbound emails count on initial load
      try {
        const inboundRes = await axios.get(`${API}/admin/inbound-emails`);
        const emails = inboundRes.data.emails || [];
        setUnreadInboundCount(emails.filter(e => !e.read).length);
      } catch (e) { /* ignore */ }
      // Fetch unread contact messages count on initial load
      try {
        const contactsRes = await axios.get(`${API}/admin/contacts`);
        const contactMsgs = contactsRes.data.messages || [];
        setUnreadContactCount(contactMsgs.filter(m => !m.read).length);
      } catch (e) { /* ignore */ }
      // Fetch popup config
      try {
        const popupRes = await axios.get(`${API}/admin/popup-config`);
        setPopupConfig(popupRes.data);
      } catch (e) { /* ignore */ }
    } catch (err) {
      toast.error('Failed to load admin data');
    } finally {
      setDataLoading(false);
    }
  };

  // Discount codes functions
  const createDiscountCode = async () => {
    if (!newCode.code || !newCode.discount_value) {
      toast.error('Code and discount value are required');
      return;
    }
    setCreatingCode(true);
    try {
      const payload = {
        ...newCode,
        discount_value: parseFloat(newCode.discount_value),
        max_uses: newCode.max_uses ? parseInt(newCode.max_uses) : null,
        expires_at: newCode.expires_at || null
      };
      const res = await axios.post(`${API}/admin/discount-codes`, payload);
      setDiscountCodes([res.data.code, ...discountCodes]);
      setNewCode({
        code: '',
        discount_type: 'percent',
        discount_value: '',
        max_uses: '',
        expires_at: '',
        valid_for_tiers: ['entry', 'pro'],
        description: '',
        new_users_only: false
      });
      toast.success('Discount code created');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to create code');
    } finally {
      setCreatingCode(false);
    }
  };

  const deleteDiscountCode = async (codeId) => {
    if (!window.confirm('Delete this discount code?')) return;
    try {
      await axios.delete(`${API}/admin/discount-codes/${codeId}`);
      setDiscountCodes(discountCodes.filter(c => c.id !== codeId));
      toast.success('Code deleted');
    } catch (err) {
      toast.error('Failed to delete code');
    }
  };

  const toggleDiscountCode = async (codeId) => {
    try {
      const res = await axios.patch(`${API}/admin/discount-codes/${codeId}`);
      setDiscountCodes(discountCodes.map(c => 
        c.id === codeId ? { ...c, is_active: res.data.is_active } : c
      ));
      toast.success(res.data.is_active ? 'Code activated' : 'Code deactivated');
    } catch (err) {
      toast.error('Failed to toggle code');
    }
  };

  const handleDeleteUser = async (userId) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('User deleted');
      // Refresh users list from server to ensure consistency
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data.users || []);
    } catch (err) {
      toast.error('Failed to delete user');
    }
  };

  const handleTogglePayments = async (enabled) => {
    try {
      await axios.post(`${API}/admin/settings`, { ...settings, payments_enabled: enabled });
      setSettings({ ...settings, payments_enabled: enabled });
      toast.success(enabled ? 'Payments enabled' : 'Payments disabled');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const handleToggleMaintenance = async (enabled) => {
    try {
      await axios.post(`${API}/admin/settings`, { ...settings, maintenance_mode: enabled });
      setSettings({ ...settings, maintenance_mode: enabled });
      toast.success(enabled ? 'Maintenance mode ON' : 'Maintenance mode OFF');
    } catch (err) {
      toast.error('Failed to update settings');
    }
  };

  const handleResetMRR = async () => {
    if (!window.confirm('Reset MRR? This will recalculate MRR based only on real Stripe payments.\n\nManual upgrades (without payment) will be excluded from MRR calculation.')) return;
    try {
      const res = await axios.post(`${API}/admin/reset-mrr`);
      toast.success(`MRR reset! Cleared ${res.data.reset_count} manual upgrade(s). Valid MRR: £${res.data.valid_mrr}`);
      // Refresh subscriptions data
      const subsRes = await axios.get(`${API}/admin/subscriptions`);
      setSubscriptions(subsRes.data);
    } catch (err) {
      toast.error('Failed to reset MRR');
    }
  };

  // Messages functions
  const fetchMessages = async () => {
    setMessagesLoading(true);
    try {
      const [contactsRes, inboundRes] = await Promise.all([
        axios.get(`${API}/admin/contacts`),
        axios.get(`${API}/admin/inbound-emails`)
      ]);
      const contactMsgs = contactsRes.data.messages || [];
      setMessages(contactMsgs);
      setUnreadContactCount(contactMsgs.filter(m => !m.read).length);
      const emails = inboundRes.data.emails || [];
      setInboundEmails(emails);
      updateUnreadCount(emails);
    } catch (err) {
      toast.error('Failed to load messages');
    } finally {
      setMessagesLoading(false);
    }
  };

  const handleReplyMessage = async (messageId) => {
    if (!replyText.trim()) {
      toast.error('Please enter a reply');
      return;
    }
    try {
      await axios.post(`${API}/admin/contacts/${messageId}/reply`, { reply: replyText });
      toast.success('Reply sent!');
      setReplyingTo(null);
      setReplyText('');
      fetchMessages();
    } catch (err) {
      toast.error('Failed to send reply');
    }
  };

  const handleDeleteMessage = async (messageId) => {
    if (!window.confirm('Delete this message?')) return;
    try {
      await axios.delete(`${API}/admin/contacts/${messageId}`);
      toast.success('Message deleted');
      const updatedMessages = messages.filter(m => m.id !== messageId);
      setMessages(updatedMessages);
      setUnreadContactCount(updatedMessages.filter(m => !m.read).length);
    } catch (err) {
      toast.error('Failed to delete message');
    }
  };

  const handleMarkContactRead = async (messageId) => {
    try {
      await axios.patch(`${API}/admin/contacts/${messageId}/read`);
      setMessages(messages.map(m => 
        m.id === messageId ? { ...m, read: true } : m
      ));
      setUnreadContactCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read');
    }
  };

  const toggleExpandMessage = (messageId) => {
    if (expandedMessageId === messageId) {
      setExpandedMessageId(null);
    } else {
      setExpandedMessageId(messageId);
      // Mark as read when expanded
      const msg = messages.find(m => m.id === messageId);
      if (msg && !msg.read) {
        handleMarkContactRead(messageId);
      }
    }
  };

  // Inbound email functions
  const handleDeleteInboundEmail = async (emailId) => {
    if (!window.confirm('Delete this customer reply?')) return;
    try {
      await axios.delete(`${API}/admin/inbound-emails/${emailId}`);
      toast.success('Reply deleted');
      setInboundEmails(inboundEmails.filter(e => e.id !== emailId));
      updateUnreadCount(inboundEmails.filter(e => e.id !== emailId));
    } catch (err) {
      toast.error('Failed to delete reply');
    }
  };

  const handleMarkInboundRead = async (emailId) => {
    try {
      await axios.patch(`${API}/admin/inbound-emails/${emailId}/read`);
      setInboundEmails(inboundEmails.map(e => 
        e.id === emailId ? { ...e, read: true } : e
      ));
      setUnreadInboundCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark as read');
    }
  };

  const toggleExpandInbound = (emailId) => {
    if (expandedInboundId === emailId) {
      setExpandedInboundId(null);
    } else {
      setExpandedInboundId(emailId);
      // Mark as read when expanded
      const email = inboundEmails.find(e => e.id === emailId);
      if (email && !email.read) {
        handleMarkInboundRead(emailId);
      }
    }
  };

  const updateUnreadCount = (emails) => {
    const count = emails.filter(e => !e.read).length;
    setUnreadInboundCount(count);
  };

  // Bulk email functions
  const fetchCampaigns = async () => {
    try {
      const res = await axios.get(`${API}/admin/email-campaigns`);
      setEmailCampaigns(res.data.campaigns || []);
    } catch (err) {
      console.error('Failed to load campaigns');
    }
  };

  const viewCampaignDetails = async (campaignId) => {
    try {
      const res = await axios.get(`${API}/admin/email-campaigns/${campaignId}`);
      setSelectedCampaign(res.data);
    } catch (err) {
      toast.error('Failed to load campaign details');
    }
  };

  const deleteCampaign = async (campaignId) => {
    if (!window.confirm('Are you sure you want to delete this campaign?')) return;
    try {
      await axios.delete(`${API}/admin/email-campaigns/${campaignId}`);
      setEmailCampaigns(emailCampaigns.filter(c => c.id !== campaignId));
      toast.success('Campaign deleted');
    } catch (err) {
      toast.error('Failed to delete campaign');
    }
  };

  // Send Offer function - prefill Messages with Enterprise quote
  const sendEnterpriseOffer = (user) => {
    const quote = user.subscription?.enterprise_quote || {};
    const price = quote.price || user.subscription?.amount_paying || 0;
    const credits = quote.credits || 0;
    const domains = quote.domains || 0;
    const firstMonthDiscount = quote.first_month_discount || 0;
    const userName = user.name || user.email?.split('@')[0] || 'there';
    
    // Calculate first month price if discount exists
    const firstMonthPrice = firstMonthDiscount > 0 
      ? Math.round(price * (1 - firstMonthDiscount / 100)) 
      : price;
    
    const discountLine = firstMonthDiscount > 0 
      ? `\n🎉 SPECIAL OFFER: ${firstMonthDiscount}% off your first month!\n   First month: £${firstMonthPrice} (then £${price}/month)\n`
      : '';
    
    const subject = firstMonthDiscount > 0 
      ? `Your ScoreCite Enterprise Quote - £${firstMonthPrice} first month (${firstMonthDiscount}% off!)`
      : `Your ScoreCite Enterprise Quote - £${price}/mo`;
    
    const body = `Hi ${userName},

Thank you for your interest in ScoreCite Enterprise!

Here are the details of your custom Enterprise package:
${discountLine}
• Regular Price: £${price}/month
• Monthly Credits: ${credits}
• Domains Included: ${domains}

This package gives you full access to all ScoreCite features including:
- Unlimited AI-powered SEO audits
- Priority support
- Custom domain tracking
- Advanced analytics

Click the button below to view your quote and complete your subscription.

Best regards,
The ScoreCite Team`;

    setEmailPrefill({
      recipient: user.email,
      subject,
      body,
      cta_text: 'View & Pay Now',
      cta_url: 'https://www.scorecite.com/pricing',
      track_clicks: true
    });
    setSelectedUser(null);
    setActiveTab('messages');
  };

  const fetchRecipientPreview = async (audience) => {
    setLoadingRecipients(true);
    try {
      const res = await axios.post(`${API}/admin/bulk-email/preview`, { audience });
      setPreviewRecipients(res.data.recipients || []);
      setSelectedRecipients(res.data.recipients.map(r => r.email));
    } catch (err) {
      toast.error('Failed to load recipients');
    } finally {
      setLoadingRecipients(false);
    }
  };

  const handleAudienceChange = (audience) => {
    setBulkEmail({...bulkEmail, audience});
    if (audience !== 'custom') {
      fetchRecipientPreview(audience);
    } else {
      setPreviewRecipients([]);
      setSelectedRecipients([]);
    }
  };

  const toggleRecipient = (email) => {
    if (selectedRecipients.includes(email)) {
      setSelectedRecipients(selectedRecipients.filter(e => e !== email));
    } else {
      setSelectedRecipients([...selectedRecipients, email]);
    }
  };

  const addCustomEmail = () => {
    const email = customEmail.trim().toLowerCase();
    if (!email || !email.includes('@')) {
      toast.error('Please enter a valid email');
      return;
    }
    if (selectedRecipients.includes(email)) {
      toast.error('Email already in list');
      return;
    }
    setSelectedRecipients([...selectedRecipients, email]);
    setPreviewRecipients([...previewRecipients, { email, name: '', tier: 'custom' }]);
    setCustomEmail('');
    setShowSuggestions(false);
    setUserSuggestions([]);
  };

  // Search users for autocomplete
  const searchUsers = async (query) => {
    if (!query || query.length < 2) {
      setUserSuggestions([]);
      setShowSuggestions(false);
      return;
    }
    setLoadingSuggestions(true);
    try {
      const res = await axios.get(`${API}/admin/users/search?q=${encodeURIComponent(query)}`);
      // Filter out already selected recipients
      const filtered = (res.data.users || []).filter(u => !selectedRecipients.includes(u.email));
      setUserSuggestions(filtered);
      setShowSuggestions(filtered.length > 0);
    } catch (err) {
      console.error('Failed to search users:', err);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Add user from suggestion
  const addUserFromSuggestion = (user) => {
    if (!selectedRecipients.includes(user.email)) {
      setSelectedRecipients([...selectedRecipients, user.email]);
      setPreviewRecipients([...previewRecipients, { email: user.email, name: user.name, tier: user.tier }]);
    }
    setCustomEmail('');
    setShowSuggestions(false);
    setUserSuggestions([]);
  };

  // Debounced search
  const handleCustomEmailChange = (e) => {
    const value = e.target.value;
    setCustomEmail(value);
    // Debounce search
    clearTimeout(window.userSearchTimeout);
    window.userSearchTimeout = setTimeout(() => searchUsers(value), 300);
  };

  const handleSendBulkEmail = async () => {
    if (!bulkEmail.subject.trim() || !bulkEmail.body.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    if (selectedRecipients.length === 0) {
      toast.error('Please select at least one recipient');
      return;
    }
    if (!window.confirm(`Send email to ${selectedRecipients.length} recipient(s)?`)) return;
    
    setSendingBulk(true);
    try {
      const res = await axios.post(`${API}/admin/bulk-email`, {
        ...bulkEmail,
        recipients: selectedRecipients.slice(0, 50)  // Max 50
      });
      
      // Show detailed result
      const { sent_count, failed_count, sent_emails, failed_emails } = res.data;
      if (failed_count > 0) {
        const failedList = failed_emails.slice(0, 3).map(f => typeof f === 'string' ? f : f.email).join(', ');
        toast.warning(
          `Sent: ${sent_count}/${sent_count + failed_count}. Failed: ${failed_count} (${failedList}${failed_count > 3 ? '...' : ''})`,
          { duration: 8000 }
        );
      } else {
        toast.success(`All ${sent_count} emails sent successfully!`);
      }
      
      setBulkEmail({ subject: '', body: '', cta_text: '', cta_url: '', audience: 'all', track_clicks: false });
      setSelectedRecipients([]);
      setPreviewRecipients([]);
      fetchCampaigns();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to send emails');
    } finally {
      setSendingBulk(false);
    }
  };

  // Fetch messages when switching to messages tab
  useEffect(() => {
    if (activeTab === 'messages' && messages.length === 0) {
      fetchMessages();
      fetchCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Apply email prefill when switching to messages tab
  useEffect(() => {
    if (activeTab === 'messages' && emailPrefill) {
      setBulkEmail({
        subject: emailPrefill.subject,
        body: emailPrefill.body,
        cta_text: emailPrefill.cta_text || '',
        cta_url: emailPrefill.cta_url || '',
        audience: 'all',
        track_clicks: emailPrefill.track_clicks || false
      });
      setSelectedRecipients([emailPrefill.recipient]);
      setPreviewRecipients([{ email: emailPrefill.recipient }]);
      setEmailPrefill(null); // Clear prefill after applying
      fetchMessages();
      fetchCampaigns();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab, emailPrefill]);

  // Fetch finance data when switching to finance tab
  const fetchFinance = async () => {
    setFinanceLoading(true);
    try {
      const res = await axios.get(`${API}/admin/finance`);
      setFinance(res.data);
      setCostInput(String(res.data.cost_per_audit || 0));
    } catch (err) {
      toast.error('Failed to load finance data');
    } finally {
      setFinanceLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === 'finance' && !finance) {
      fetchFinance();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  const handleSaveCost = async () => {
    setSavingCost(true);
    try {
      const val = parseFloat(costInput) || 0;
      await axios.post(`${API}/admin/finance/cost`, { cost_per_audit: val });
      toast.success(`Audit cost updated: £${val.toFixed(2)}`);
      setEditingCost(false);
      fetchFinance();
    } catch (err) {
      toast.error('Failed to save cost');
    } finally {
      setSavingCost(false);
    }
  };

  const fetchAudits = async (page = 0, filters = auditsFilter) => {
    setAuditsLoading(true);
    try {
      const params = new URLSearchParams();
      const hasFilters = filters.search || filters.dateFrom || filters.dateTo || filters.status;
      
      if (hasFilters) {
        // With filters, use pagination
        params.append('skip', page * 50);
        params.append('limit', 50);
      }
      // Without filters, don't send limit - backend will return all audits
      
      if (filters.search) params.append('search', filters.search);
      if (filters.dateFrom) params.append('date_from', filters.dateFrom);
      if (filters.dateTo) params.append('date_to', filters.dateTo);
      if (filters.status) params.append('status_filter', filters.status);
      
      const res = await axios.get(`${API}/admin/audits?${params.toString()}`);
      setAllAudits(res.data.audits || []);
      setAuditsTotal(res.data.total || 0);
      setAuditsPage(hasFilters ? page : 0);
    } catch (err) {
      toast.error('Failed to load audits');
    } finally {
      setAuditsLoading(false);
    }
  };

  const handleDeleteAudit = async (auditId) => {
    const audit = allAudits.find(a => a.id === auditId);
    if (audit?.is_locked) {
      toast.error('This audit is locked. Unlock it first to delete.');
      return;
    }
    setDeleteAuditTarget(audit);
  };

  const confirmDeleteAudit = async () => {
    if (!deleteAuditTarget) return;
    setDeletingAudit(true);
    try {
      await axios.delete(`${API}/admin/audits/${deleteAuditTarget.id}`);
      toast.success('Audit deleted');
      setDeleteAuditTarget(null);
      fetchAudits(auditsPage, auditsFilter);
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete audit');
    } finally {
      setDeletingAudit(false);
    }
  };

  // Toggle audit selection
  const toggleAuditSelection = (auditId) => {
    setSelectedAuditIds(prev => 
      prev.includes(auditId) 
        ? prev.filter(id => id !== auditId) 
        : [...prev, auditId]
    );
  };

  // Select/deselect all visible audits
  const toggleSelectAllAudits = () => {
    if (selectedAuditIds.length === allAudits.length) {
      setSelectedAuditIds([]);
    } else {
      setSelectedAuditIds(allAudits.map(a => a.id));
    }
  };

  // Bulk delete selected audits
  const handleBulkDeleteAudits = async () => {
    if (selectedAuditIds.length === 0) {
      toast.error('No audits selected');
      return;
    }
    setBulkDeleteOpen(true);
  };

  const confirmBulkDelete = async () => {
    setBulkDeleteOpen(false);
    setDeletingAudits(true);
    try {
      const res = await axios.post(`${API}/admin/audits/bulk-delete`, { audit_ids: selectedAuditIds });
      toast.success(`Deleted ${res.data.deleted_count} audits${res.data.skipped_locked ? ` (${res.data.skipped_locked} locked skipped)` : ''}`);
      setSelectedAuditIds([]);
      fetchAudits(auditsPage, auditsFilter);
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete audits');
    } finally {
      setDeletingAudits(false);
    }
  };

  // Delete all filtered audits
  const handleDeleteFilteredAudits = async () => {
    setFilterDeleteOpen(true);
  };

  const confirmFilterDelete = async () => {
    setFilterDeleteOpen(false);
    setDeletingAudits(true);
    try {
      const res = await axios.post(`${API}/admin/audits/delete-filtered`, {
        user_filter: auditsFilter.user,
        date_from: auditsFilter.dateFrom,
        date_to: auditsFilter.dateTo,
        status_filter: auditsFilter.status
      });
      toast.success(`Deleted ${res.data.deleted_count} audits${res.data.skipped_locked ? ` (${res.data.skipped_locked} locked skipped)` : ''}`);
      setSelectedAuditIds([]);
      fetchAudits(0, auditsFilter);
      const statsRes = await axios.get(`${API}/admin/stats`);
      setStats(statsRes.data);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to delete audits');
    } finally {
      setDeletingAudits(false);
    }
  };

  // Toggle audit lock
  const handleToggleAuditLock = async (auditId, currentlyLocked) => {
    try {
      await axios.post(`${API}/admin/audits/${auditId}/lock`, { lock: !currentlyLocked });
      toast.success(currentlyLocked ? 'Audit unlocked' : 'Audit locked');
      // Update local state
      setAllAudits(prev => prev.map(a => 
        a.id === auditId ? { ...a, is_locked: !currentlyLocked } : a
      ));
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to toggle lock');
    }
  };

  // Copy audit link and auto-lock
  const handleCopyAuditLink = async (audit, trackClicks = false) => {
    const frontendUrl = window.location.origin;
    
    // Both links point to frontend - tracking link includes ?track=1 parameter
    // Frontend will call tracking API when page loads with this parameter
    const link = trackClicks 
      ? `${frontendUrl}/report/${audit.id}?track=1`
      : `${frontendUrl}/report/${audit.id}`;
    
    try {
      await navigator.clipboard.writeText(link);
      // Record copy and auto-lock on server
      await axios.post(`${API}/admin/audits/${audit.id}/copy-link?track_clicks=${trackClicks}`);
      toast.success(trackClicks ? 'Tracking link copied & audit locked' : 'Link copied & audit locked');
      // Update local state — only modify tracking fields when copying tracking link
      setAllAudits(prev => prev.map(a => 
        a.id === audit.id ? { 
          ...a, 
          is_locked: true, 
          link_copied_at: new Date().toISOString(),
          ...(trackClicks ? { track_clicks: true, link_clicked_at: null } : {})
        } : a
      ));
    } catch (err) {
      // Fallback for clipboard
      const textArea = document.createElement('textarea');
      textArea.value = link;
      document.body.appendChild(textArea);
      textArea.select();
      document.execCommand('copy');
      document.body.removeChild(textArea);
      toast.success('Link copied');
    }
  };

  // Cancel running audit
  const handleCancelAudit = async (auditId) => {
    if (!window.confirm('Cancel this audit? Credit will be refunded if <50% complete.')) return;
    try {
      const res = await axios.post(`${API}/audit/${auditId}/cancel`);
      toast.success(res.data.refunded ? 'Audit cancelled. Credit refunded.' : 'Audit cancelled.');
      fetchAudits(auditsPage, auditsFilter);
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to cancel audit');
    }
  };

  const handleAuditsFilterChange = (key, value) => {
    const newFilters = { ...auditsFilter, [key]: value };
    setAuditsFilter(newFilters);
    fetchAudits(0, newFilters);
  };

  const clearAuditsFilters = () => {
    const emptyFilters = { search: '', dateFrom: '', dateTo: '', status: '' };
    setAuditsFilter(emptyFilters);
    fetchAudits(0, emptyFilters);
  };

  // Fetch audits when switching to audits tab
  useEffect(() => {
    if (activeTab === 'audits' && allAudits.length === 0) {
      fetchAudits();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeTab]);

  // Poll for new messages every 30s (so badge updates without manual refresh)
  useEffect(() => {
    const pollUnread = async () => {
      try {
        const [contactsRes, inboundRes] = await Promise.all([
          axios.get(`${API}/admin/contacts`),
          axios.get(`${API}/admin/inbound-emails`)
        ]);
        setUnreadContactCount((contactsRes.data.messages || []).filter(m => !m.read).length);
        setUnreadInboundCount((inboundRes.data.emails || []).filter(e => !e.read).length);
      } catch (e) { /* ignore */ }
    };
    const interval = setInterval(pollUnread, 30000);
    return () => clearInterval(interval);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleUpgradeUser = async () => {
    if (!upgradeUser) return;
    try {
      const userId = upgradeUser.id || upgradeUser.user_id;
      const upgradeData = { tier: upgradeTier };
      
      // For enterprise, include custom configuration
      if (upgradeTier === 'enterprise') {
        if (enterpriseConfig.custom_price) {
          upgradeData.custom_price = parseFloat(enterpriseConfig.custom_price);
        }
        if (enterpriseConfig.custom_credits) {
          upgradeData.custom_credits = parseInt(enterpriseConfig.custom_credits);
        }
        if (enterpriseConfig.custom_domains) {
          upgradeData.custom_domains = parseInt(enterpriseConfig.custom_domains);
        }
        if (enterpriseConfig.first_month_discount) {
          upgradeData.first_month_discount = parseInt(enterpriseConfig.first_month_discount);
        }
      }
      
      await axios.post(`${API}/admin/user/${userId}/upgrade`, upgradeData);
      toast.success(`User upgraded to ${upgradeTier}${upgradeTier === 'enterprise' ? ' with custom config' : ''}`);
      setUpgradeUser(null);
      setEnterpiseConfig({ custom_price: '', custom_credits: '', custom_domains: '', first_month_discount: '' });
      fetchData(); // Refresh data
    } catch (err) {
      toast.error('Failed to upgrade user');
    }
  };

  // Enterprise config state
  const [enterpriseConfig, setEnterpiseConfig] = useState({
    custom_price: '',
    custom_credits: '',
    custom_domains: '',
    first_month_discount: ''
  });

  const handleAddCredits = async () => {
    if (!addCreditsUser || creditsToAdd === 0) return;
    try {
      const userId = addCreditsUser.id || addCreditsUser.user_id;
      await axios.post(`${API}/admin/user/${userId}/add-credits`, { credits: creditsToAdd });
      const action = creditsToAdd > 0 ? 'Added' : 'Subtracted';
      toast.success(`${action} ${Math.abs(creditsToAdd)} credits`);
      setAddCreditsUser(null);
      setCreditsToAdd(1);
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to modify credits');
    }
  };

  const handleResetPassword = async () => {
    if (!resetPasswordUser || !newPassword) return;
    try {
      const userId = resetPasswordUser.id || resetPasswordUser.user_id;
      await axios.post(`${API}/admin/user/${userId}/reset-password`, { new_password: newPassword });
      toast.success('Password reset successfully');
      setResetPasswordUser(null);
      setNewPassword('');
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to reset password');
    }
  };

  const handleBlockUser = async (block = true) => {
    if (!blockUser) return;
    try {
      const userId = blockUser.id || blockUser.user_id;
      await axios.post(`${API}/admin/user/${userId}/block`, { block, reason: blockReason });
      toast.success(block ? 'User blocked' : 'User unblocked');
      setBlockUser(null);
      setBlockReason('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to update user status');
    }
  };

  const handleDeletePayment = async (paymentId) => {
    if (!window.confirm('Delete this payment record?')) return;
    try {
      await axios.delete(`${API}/admin/payment/${paymentId}`);
      toast.success('Payment deleted');
      fetchData();
    } catch (err) {
      toast.error('Failed to delete payment');
    }
  };

  const handleClearAllPayments = async () => {
    if (!window.confirm('⚠️ DELETE ALL PAYMENT RECORDS? This cannot be undone!')) return;
    if (!window.confirm('Are you REALLY sure? Type "DELETE" in your mind and click OK.')) return;
    try {
      await axios.delete(`${API}/admin/payments/clear-all`);
      toast.success('All payments cleared');
      fetchData();
    } catch (err) {
      toast.error('Failed to clear payments');
    }
  };

  const handleSetStripeCustomer = async () => {
    if (!stripeCustomerId || !stripeCustomerId.startsWith('cus_')) {
      toast.error('Customer ID must start with cus_');
      return;
    }
    try {
      await axios.post(`${API}/admin/user/${stripeCustomerUser.id}/set-stripe-customer`, {
        customer_id: stripeCustomerId
      });
      toast.success('Stripe Customer ID set successfully');
      setStripeCustomerUser(null);
      setStripeCustomerId('');
      fetchData();
    } catch (err) {
      toast.error(err.response?.data?.detail || 'Failed to set customer ID');
    }
  };

  // Pricing management functions
  const updatePricingTier = (tierId, field, value) => {
    setPricing(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tierId]: {
          ...prev.tiers[tierId],
          [field]: value
        }
      }
    }));
  };

  const updatePricingFeature = (tierId, index, value) => {
    setPricing(prev => {
      const features = [...(prev.tiers[tierId].features || [])];
      features[index] = value;
      return {
        ...prev,
        tiers: {
          ...prev.tiers,
          [tierId]: {
            ...prev.tiers[tierId],
            features
          }
        }
      };
    });
  };

  const addPricingFeature = (tierId) => {
    setPricing(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tierId]: {
          ...prev.tiers[tierId],
          features: [...(prev.tiers[tierId].features || []), 'New feature']
        }
      }
    }));
  };

  const removePricingFeature = (tierId, index) => {
    setPricing(prev => ({
      ...prev,
      tiers: {
        ...prev.tiers,
        [tierId]: {
          ...prev.tiers[tierId],
          features: prev.tiers[tierId].features.filter((_, i) => i !== index)
        }
      }
    }));
  };

  const savePricing = async () => {
    setSavingPricing(true);
    try {
      await axios.post(`${API}/admin/pricing`, pricing);
      toast.success('Pricing updated successfully');
    } catch (err) {
      toast.error('Failed to save pricing');
    } finally {
      setSavingPricing(false);
    }
  };

  const viewUserDetails = async (userId) => {
    try {
      const res = await axios.get(`${API}/admin/users/${userId}`);
      setSelectedUser(res.data);
    } catch (err) {
      toast.error('Failed to load user details');
    }
  };

  if (!isAdmin) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center p-4">
        <Card className="bg-[#121212] border-white/10 max-w-md">
          <CardContent className="p-8 text-center">
            <Shield className="w-16 h-16 text-red-400 mx-auto mb-4" />
            <h2 className="text-xl font-bold text-white mb-2">Access Denied</h2>
            <p className="text-zinc-400 mb-4">You don't have admin privileges.</p>
            <Link to="/">
              <Button variant="outline" className="border-white/20 text-white">Go Home</Button>
            </Link>
          </CardContent>
        </Card>
      </div>
    );
  }

  if (authLoading || dataLoading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] p-6">
        <div className="max-w-7xl mx-auto space-y-6">
          <Skeleton className="h-10 w-64 bg-[#1A1A1A]" />
          <div className="grid grid-cols-3 gap-4">
            {[1, 2, 3].map(i => <Skeleton key={i} className="h-28 bg-[#1A1A1A]" />)}
          </div>
          <Skeleton className="h-96 bg-[#1A1A1A]" />
        </div>
      </div>
    );
  }

  return (
    <AppLayout breadcrumbs={[
      { label: 'Dashboard', href: '/dashboard' },
      { label: 'Admin Panel' }
    ]}>
      <Helmet>
        <title>Admin Panel | ScoreCite</title>
        <meta name="robots" content="noindex, nofollow" />
      </Helmet>
      <div className="p-3 sm:p-4 md:p-6">
        <div className="mx-auto w-full max-w-[1600px]">
          {/* Header */}
          <div className="flex items-center justify-between mb-4 sm:mb-6 gap-2">
            <div className="min-w-0 flex-1">
              <h1 className="text-lg sm:text-xl md:text-2xl font-bold text-white truncate">Admin Dashboard</h1>
            </div>
            <Badge className="bg-purple-600 text-white flex-shrink-0 text-xs">
              <Shield className="w-3 h-3 mr-1" /> Admin
            </Badge>
          </div>

          {/* Tabs - horizontal scroll on mobile, single row on desktop */}
          <div className="mb-4 sm:mb-6">
            <div className="flex overflow-x-auto pb-2 -mx-3 px-3 sm:mx-0 sm:px-0 sm:pb-0 gap-2 scrollbar-hide">
              {['overview', 'audits', 'users', 'messages', 'payments', 'finance', 'discounts', 'pricing', 'settings'].map(tab => (
                <Button
                  key={tab}
                  variant={activeTab === tab ? 'default' : 'ghost'}
                  size="sm"
                  onClick={() => setActiveTab(tab)}
                  className={`text-sm px-3 whitespace-nowrap flex-shrink-0 ${activeTab === tab ? 'bg-blue-600' : 'text-zinc-400 hover:text-white'} ${tab === 'messages' && (unreadInboundCount + unreadContactCount) > 0 && activeTab !== 'messages' ? 'text-yellow-400 font-semibold' : ''}`}
                >
                  {tab.charAt(0).toUpperCase() + tab.slice(1)}
                  {tab === 'messages' && (unreadInboundCount + unreadContactCount) > 0 && (
                    <span className="ml-2 bg-yellow-500 text-black text-[10px] font-bold px-1.5 py-0.5 rounded-full">{unreadInboundCount + unreadContactCount}</span>
                  )}
                </Button>
              ))}
            </div>
          </div>

          {/* Stats Row - 2x2 grid on larger screens, vertical on small */}
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 sm:gap-3 mb-6">
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-blue-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <Users className="w-3 h-3 sm:w-4 sm:h-4 text-blue-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-zinc-500 text-[9px] sm:text-[10px]">Users</p>
                  <p className="text-base sm:text-lg font-bold text-white">{stats?.users_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-green-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <BarChart2 className="w-3 h-3 sm:w-4 sm:h-4 text-green-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-zinc-500 text-[9px] sm:text-[10px]">Audits</p>
                  <p className="text-base sm:text-lg font-bold text-white">{stats?.audits_count || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-yellow-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <CheckSquare className="w-3 h-3 sm:w-4 sm:h-4 text-yellow-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-zinc-500 text-[9px] sm:text-[10px]">Completed</p>
                  <p className="text-base sm:text-lg font-bold text-white">{stats?.completed_audits || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
          <Card className="bg-[#121212] border-white/10">
            <CardContent className="p-2 sm:p-3">
              <div className="flex items-center gap-2">
                <div className="w-6 h-6 sm:w-8 sm:h-8 bg-purple-500/10 rounded-lg flex items-center justify-center flex-shrink-0">
                  <TrendingUp className="w-3 h-3 sm:w-4 sm:h-4 text-purple-400" />
                </div>
                <div className="min-w-0">
                  <p className="text-zinc-500 text-[9px] sm:text-[10px]">Active Subs</p>
                  <p className="text-base sm:text-lg font-bold text-white">{subscriptions?.active_subscriptions || 0}</p>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Subscription Distribution */}
        {activeTab === 'overview' && subscriptions && (
          <Card className="bg-[#121212] border-white/10 mb-6">
            <CardHeader className="pb-2">
              <CardTitle className="text-white text-base sm:text-lg">Plan distribution</CardTitle>
              <p className="text-zinc-500 text-xs sm:text-sm">Active paid subscriptions only</p>
            </CardHeader>
            <CardContent>
              <div className="flex flex-wrap gap-4 sm:gap-6 mb-4">
                {Object.entries(subscriptions.tier_distribution || {}).map(([tier, count]) => {
                  const displayTier = tier === 'entry' ? 'core' : tier;
                  const pendingCount = subscriptions.pending_distribution?.[tier] || 0;
                  return (
                    <div key={tier} className="text-center">
                      <p className="text-3xl font-bold text-white">{count}</p>
                      <p className={`text-sm capitalize ${
                        displayTier === 'enterprise' ? 'text-purple-400' :
                        displayTier === 'pro' ? 'text-blue-400' :
                        displayTier === 'core' ? 'text-green-400' : 'text-zinc-400'
                      }`}>{displayTier}</p>
                      {pendingCount > 0 && (
                        <p className="text-xs text-yellow-400">+{pendingCount} pending</p>
                      )}
                    </div>
                  );
                })}
              </div>
              {/* Summary */}
              <div className="pt-4 border-t border-white/10 flex gap-6 text-sm">
                <div>
                  <span className="text-zinc-500">Total Active:</span>
                  <span className="text-white ml-2 font-medium">{subscriptions.active_subscriptions}</span>
                </div>
                <div>
                  <span className="text-zinc-500">Pending Payment:</span>
                  <span className="text-yellow-400 ml-2 font-medium">
                    {Object.values(subscriptions.pending_distribution || {}).reduce((a, b) => a + b, 0)}
                  </span>
                </div>
                <div>
                  <span className="text-zinc-500">Total Users:</span>
                  <span className="text-white ml-2 font-medium">{subscriptions.total_users}</span>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Users Table - Different view for Overview vs Users tab */}
        {(activeTab === 'overview' || activeTab === 'users') && (
        <Card className="bg-[#121212] border-white/10 mb-8">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <Users className="w-5 h-5" /> 
              {activeTab === 'overview' ? 'Recent Users (Last 7 Days)' : 'All Users'}
            </CardTitle>
          </CardHeader>
          <CardContent>
            {/* Search bar - only in Users tab */}
            {activeTab === 'users' && (
              <div className="mb-4">
                <Input
                  placeholder="Search by email, name, plan, status..."
                  value={usersSearch}
                  onChange={(e) => setUsersSearch(e.target.value)}
                  className="bg-[#0A0A0A] border-white/10 text-white max-w-md"
                />
              </div>
            )}
            {/* Mobile Cards - hidden on desktop */}
            <div className="md:hidden space-y-3">
              {(subscriptions?.users_with_subscription || users)
                .filter(u => {
                  if (activeTab === 'overview') {
                    const sevenDaysAgo = new Date();
                    sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                    const userCreated = u.created_at ? new Date(u.created_at) : null;
                    if (!userCreated || userCreated < sevenDaysAgo) return false;
                  }
                  if (activeTab === 'users' && usersSearch.trim()) {
                    const s = usersSearch.toLowerCase();
                    return u.email?.toLowerCase().includes(s) ||
                           u.name?.toLowerCase().includes(s) ||
                           u.tier?.toLowerCase().includes(s);
                  }
                  return true;
                })
                .sort((a, b) => {
                  if (activeTab === 'overview') {
                    const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                    const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                    return dateB - dateA;
                  }
                  return 0;
                })
                .map(u => {
                  const isPending = u.enterprise_config?.status === 'pending_payment';
                  const isActive = u.subscription_status === 'active' || u.current_period_end;
                  return (
                    <div key={u.id} className={`rounded-lg border border-white/10 bg-[#1A1A1A] p-3 ${u.is_blocked ? 'opacity-50' : ''}`}>
                      {/* Actions row at top */}
                      <div className="flex items-center justify-end gap-1 mb-2 pb-2 border-b border-white/5">
                        <Button variant="ghost" size="sm" onClick={() => viewUserDetails(u.id)} className="h-8 w-8 p-0" title="View details">
                          <Eye className="w-4 h-4 text-zinc-400" />
                        </Button>
                        {!u.is_admin && (
                          <>
                            <Button variant="ghost" size="sm" onClick={() => { setUpgradeUser(u); setUpgradeTier(u.tier === 'pro' ? 'core' : 'pro'); }} className="h-8 w-8 p-0" title="Change tier">
                              <ArrowUp className="w-4 h-4 text-blue-400" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setAddCreditsUser(u)} className="h-8 w-8 p-0" title="Add credits">
                              <Plus className="w-4 h-4 text-green-400" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setResetPasswordUser(u)} className="h-8 w-8 p-0" title="Reset password">
                              <Key className="w-4 h-4 text-yellow-400" />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => setBlockUser(u)} className="h-8 w-8 p-0" title={u.is_blocked ? "Unblock" : "Block"}>
                              <Ban className={`w-4 h-4 ${u.is_blocked ? 'text-green-400' : 'text-orange-400'}`} />
                            </Button>
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteUser(u.id)} className="h-8 w-8 p-0" title="Delete">
                              <Trash2 className="w-4 h-4 text-red-400" />
                            </Button>
                          </>
                        )}
                      </div>
                      {/* User data below */}
                      <div className="text-sm text-white font-medium break-all">{u.email}</div>
                      <div className="text-xs text-zinc-500">{u.name || '-'}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Badge className={
                          u.tier === 'enterprise' ? 'bg-purple-600' :
                          u.tier === 'pro' ? 'bg-blue-600' :
                          u.tier === 'core' ? 'bg-green-600' : 'bg-zinc-600'
                        }>
                          {u.tier || 'free'}
                        </Badge>
                        <Badge className={
                          u.is_blocked ? 'bg-red-600' :
                          u.is_admin ? 'bg-purple-600' :
                          isPending ? 'bg-yellow-600' :
                          isActive ? 'bg-green-600' : 'bg-zinc-600'
                        }>
                          {u.is_blocked ? 'Blocked' : u.is_admin ? 'Admin' : isPending ? 'Pending' : isActive ? 'Active' : 'Free'}
                        </Badge>
                        <span className="text-xs text-zinc-500">{u.is_admin ? '∞' : (u.credits_remaining || 0)} credits</span>
                      </div>
                    </div>
                  );
                })}
            </div>

            {/* Desktop Table - hidden on mobile */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-zinc-400">Email</TableHead>
                  <TableHead className="text-zinc-400">Name</TableHead>
                  <TableHead className="text-zinc-400">Plan</TableHead>
                  <TableHead className="text-zinc-400">Amount</TableHead>
                  <TableHead className="text-zinc-400">Credits</TableHead>
                  <TableHead className="text-zinc-400">Last Payment</TableHead>
                  <TableHead className="text-zinc-400">Next Due</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(subscriptions?.users_with_subscription || users)
                  .filter(u => {
                    // In Overview tab: only show users from last 7 days
                    if (activeTab === 'overview') {
                      const sevenDaysAgo = new Date();
                      sevenDaysAgo.setDate(sevenDaysAgo.getDate() - 7);
                      const userCreated = u.created_at ? new Date(u.created_at) : null;
                      if (!userCreated || userCreated < sevenDaysAgo) return false;
                    }
                    
                    // Search filter (only in Users tab)
                    if (activeTab === 'users' && usersSearch.trim()) {
                      const search = usersSearch.toLowerCase();
                      const displayTier = u.tier === 'entry' ? 'core' : (u.tier || 'free');
                      const subStatus = u.subscription_status || (u.tier === 'free' ? 'none' : 'unknown');
                      return (
                        u.email?.toLowerCase().includes(search) ||
                        u.name?.toLowerCase().includes(search) ||
                        displayTier.toLowerCase().includes(search) ||
                        subStatus.toLowerCase().includes(search)
                      );
                    }
                    return true;
                  })
                  .sort((a, b) => {
                    if (activeTab === 'overview') {
                      const dateA = a.created_at ? new Date(a.created_at) : new Date(0);
                      const dateB = b.created_at ? new Date(b.created_at) : new Date(0);
                      return dateB - dateA;
                    }
                    return 0;
                  })
                  .map(u => {
                  // Calculate days left
                  let daysLeft = null;
                  if (u.current_period_end && u.tier !== 'free') {
                    const endDate = new Date(u.current_period_end);
                    const now = new Date();
                    daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                  }
                  
                  // Determine subscription status for display
                  const subStatus = u.subscription_status || (u.tier === 'free' ? 'none' : 'unknown');
                  const isPending = subStatus === 'pending_payment';
                  const isActive = subStatus === 'active';
                  const isCancelled = subStatus === 'cancelled';
                  const isExpired = subStatus === 'expired' || (daysLeft !== null && daysLeft < 0);
                  
                  // Get display tier (handle entry -> core)
                  const displayTier = u.tier === 'entry' ? 'core' : (u.tier || 'free');
                  
                  return (
                  <TableRow key={u.id} className={`border-white/10 ${u.is_blocked ? 'opacity-50' : ''}`}>
                    <TableCell className="text-white font-medium">{u.email}</TableCell>
                    <TableCell className="text-zinc-300">{u.name || '-'}</TableCell>
                    <TableCell>
                      <Badge className={
                        isPending ? 'bg-yellow-600' :
                        displayTier === 'enterprise' ? 'bg-purple-600' :
                        displayTier === 'pro' ? 'bg-blue-600' :
                        displayTier === 'core' ? 'bg-green-600' : 'bg-zinc-600'
                      }>
                        {displayTier}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-300">
                      {u.tier === 'free' ? '-' : u.enterprise_config?.custom_price ? `£${u.enterprise_config.custom_price}` : u.amount_paid ? `£${u.amount_paid}` : '-'}
                    </TableCell>
                    <TableCell className="text-zinc-300">{u.is_admin ? '∞' : (u.credits_remaining || 0)}</TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {u.last_payment_date ? new Date(u.last_payment_date).toLocaleDateString('pl-PL') : 'Never'}
                    </TableCell>
                    <TableCell className="text-sm">
                      {u.current_period_end ? new Date(u.current_period_end).toLocaleDateString('pl-PL') : '-'}
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        u.is_blocked ? 'bg-red-600' :
                        u.is_admin ? 'bg-purple-600' :
                        isPending ? 'bg-yellow-600' :
                        isActive ? 'bg-green-600' : 'bg-zinc-600'
                      }>
                        {u.is_blocked ? 'Blocked' : u.is_admin ? 'Admin' : isPending ? 'Pending' : isActive ? 'Active' : 'Free'}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-right">
                      <div className="flex justify-end gap-1">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => viewUserDetails(u.id)}
                          className="text-zinc-400 hover:text-white"
                          title="View details"
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {!u.is_admin && (
                          <>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => {
                                setUpgradeUser(u);
                                setUpgradeTier(u.tier === 'pro' ? 'core' : 'pro');
                              }}
                              className="text-blue-400 hover:text-blue-300"
                              title="Change tier"
                            >
                              <ArrowUp className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setAddCreditsUser(u)}
                              className="text-green-400 hover:text-green-300"
                              title="Add credits"
                            >
                              <Plus className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setResetPasswordUser(u)}
                              className="text-yellow-400 hover:text-yellow-300"
                              title="Reset password"
                            >
                              <Key className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => { setStripeCustomerUser(u); setStripeCustomerId(u.customer_id || u.subscription?.customer_id || ''); }}
                              className="text-blue-400 hover:text-blue-300"
                              title="Set Stripe Customer ID"
                            >
                              <CreditCard className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => setBlockUser(u)}
                              className={u.is_blocked ? "text-green-400 hover:text-green-300" : "text-orange-400 hover:text-orange-300"}
                              title={u.is_blocked ? "Unblock user" : "Block user"}
                            >
                              <Ban className="w-4 h-4" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="sm"
                              onClick={() => handleDeleteUser(u.id)}
                              className="text-red-400 hover:text-red-300"
                              title="Delete user"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  </TableRow>
                  );
                })}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Payments Table */}
        {activeTab === 'payments' && payments && (
          <Card className="bg-[#121212] border-white/10 mb-8">
            <CardHeader className="flex flex-row items-center justify-between">
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" /> Payment history
              </CardTitle>
              {payments.payments?.length > 0 && (
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleClearAllPayments}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10"
                >
                  <Trash2 className="w-4 h-4 mr-2" /> Clear All
                </Button>
              )}
            </CardHeader>
            <CardContent>
              {(!payments.payments || payments.payments.length === 0) ? (
                <p className="text-center text-zinc-500 py-8">No payments yet</p>
              ) : (
                <>
                  {/* Mobile Cards - hidden on desktop */}
                  <div className="md:hidden space-y-3">
                    {payments.payments?.map(p => (
                      <div key={p.id} className="rounded-lg border border-white/10 bg-[#1A1A1A] p-3">
                        <div className="flex items-start justify-between gap-3">
                          <div className="min-w-0 flex-1">
                            <div className="text-sm text-white font-medium break-all">{p.user_email}</div>
                            <div className="mt-2 flex flex-wrap items-center gap-2">
                              <Badge className={
                                p.tier === 'pro' ? 'bg-purple-600' :
                                p.tier === 'entry' ? 'bg-blue-600' : 'bg-zinc-600'
                              }>
                                {p.tier}
                              </Badge>
                              <Badge className={
                                p.payment_status === 'paid' ? 'bg-green-600' :
                                p.payment_status === 'pending' ? 'bg-yellow-600' : 'bg-red-600'
                              }>
                                {p.payment_status === 'paid' ? 'Paid' : p.payment_status === 'pending' ? 'Pending' : 'Cancelled'}
                              </Badge>
                              {p.discount_code && (
                                <Badge className="bg-green-600/20 text-green-400 font-mono text-xs">{p.discount_code}</Badge>
                              )}
                            </div>
                            <div className="mt-2 flex items-center gap-3 text-sm">
                              <span className="text-zinc-300">
                                {p.discount_code ? (
                                  <>
                                    <span className="line-through text-zinc-500">£{p.amount}</span>
                                    <span className="text-green-400 ml-1">£{p.final_amount || (p.amount - (p.discount_amount || 0))}</span>
                                  </>
                                ) : `£${p.amount}`}
                              </span>
                              <span className="text-zinc-500">{p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}</span>
                            </div>
                          </div>
                          <Button variant="ghost" size="sm" onClick={() => handleDeletePayment(p.id)} className="h-9 w-9 p-0 shrink-0">
                            <Trash2 className="w-4 h-4 text-red-400" />
                          </Button>
                        </div>
                      </div>
                    ))}
                  </div>
                  
                  {/* Desktop Table - hidden on mobile */}
                  <div className="hidden md:block">
                    <Table>
                      <TableHeader>
                        <TableRow className="border-white/10">
                          <TableHead className="text-zinc-400">Email</TableHead>
                          <TableHead className="text-zinc-400">Plan</TableHead>
                          <TableHead className="text-zinc-400">Amount</TableHead>
                          <TableHead className="text-zinc-400">Discount</TableHead>
                          <TableHead className="text-zinc-400">Status</TableHead>
                          <TableHead className="text-zinc-400">Date</TableHead>
                          <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                        </TableRow>
                      </TableHeader>
                      <TableBody>
                        {payments.payments?.map(p => (
                          <TableRow key={p.id} className="border-white/10">
                            <TableCell className="text-white font-medium">{p.user_email}</TableCell>
                            <TableCell>
                              <Badge className={
                                p.tier === 'pro' ? 'bg-purple-600' :
                                p.tier === 'entry' ? 'bg-blue-600' : 'bg-zinc-600'
                              }>
                                {p.tier}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-zinc-300">
                              {p.discount_code ? (
                                <div>
                                  <span className="line-through text-zinc-500">£{p.amount}</span>
                                  <span className="text-green-400 ml-2">£{p.final_amount || (p.amount - (p.discount_amount || 0))}</span>
                                </div>
                              ) : (
                                `£${p.amount}`
                              )}
                            </TableCell>
                            <TableCell>
                              {p.discount_code ? (
                                <Badge className="bg-green-600/20 text-green-400 font-mono text-xs">
                                  {p.discount_code}
                                </Badge>
                              ) : (
                                <span className="text-zinc-600">-</span>
                              )}
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                p.payment_status === 'paid' ? 'bg-green-600' :
                                p.payment_status === 'pending' ? 'bg-yellow-600' : 'bg-red-600'
                              }>
                              {p.payment_status === 'paid' ? 'Paid' :
                                 p.payment_status === 'pending' ? 'Pending' : 'Cancelled'}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-zinc-400 text-sm">
                              {p.created_at ? new Date(p.created_at).toLocaleDateString() : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <Button
                                variant="ghost"
                                size="sm"
                                onClick={() => handleDeletePayment(p.id)}
                                className="text-red-400 hover:text-red-300"
                              >
                                <Trash2 className="w-4 h-4" />
                              </Button>
                            </TableCell>
                          </TableRow>
                        ))}
                      </TableBody>
                    </Table>
                  </div>
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* ===== FINANCE TAB ===== */}
        {activeTab === 'finance' && (
          <div className="space-y-6">
            {financeLoading ? (
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                {[...Array(8)].map((_, i) => <Skeleton key={i} className="h-24 bg-[#1A1A1A]" />)}
              </div>
            ) : finance ? (
              <>
                {/* Revenue Overview */}
                <Card className="bg-[#121212] border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center gap-2 text-base">
                      <PoundSterling className="w-5 h-5 text-green-400" /> Revenue Overview
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-green-500/5 border border-green-500/10">
                        <p className="text-zinc-500 text-xs">Total Revenue</p>
                        <p className="text-xl font-bold text-green-400" data-testid="finance-total-revenue">£{finance.total_revenue.toFixed(2)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-blue-500/5 border border-blue-500/10">
                        <p className="text-zinc-500 text-xs">This Month</p>
                        <p className="text-xl font-bold text-blue-400" data-testid="finance-monthly-revenue">£{finance.revenue_this_month.toFixed(2)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-purple-500/5 border border-purple-500/10">
                        <p className="text-zinc-500 text-xs">MRR</p>
                        <p className="text-xl font-bold text-purple-400" data-testid="finance-mrr">£{finance.mrr}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-cyan-500/5 border border-cyan-500/10">
                        <p className="text-zinc-500 text-xs">Active Subscriptions</p>
                        <p className="text-xl font-bold text-cyan-400" data-testid="finance-active-subs">{finance.active_subscriptions}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* API Cost & Profitability */}
                <Card className="bg-[#121212] border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center gap-2 text-base">
                      <Settings className="w-5 h-5 text-yellow-400" /> Audit Cost & Profitability
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    {/* Cost per audit setting */}
                    <div className="flex flex-col sm:flex-row items-start sm:items-center gap-3 mb-6 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                      <div className="flex-1">
                        <p className="text-sm text-white font-medium">Cost per audit (API)</p>
                        <p className="text-xs text-zinc-500">Average cost of LLM API calls + crawling per single audit</p>
                      </div>
                      {editingCost ? (
                        <div className="flex items-center gap-2">
                          <span className="text-zinc-400">£</span>
                          <Input
                            type="number"
                            step="0.01"
                            min="0"
                            value={costInput}
                            onChange={(e) => setCostInput(e.target.value)}
                            className="w-24 bg-[#0A0A0A] border-white/10 text-white h-8"
                            data-testid="cost-per-audit-input"
                          />
                          <Button size="sm" onClick={handleSaveCost} disabled={savingCost} className="bg-green-600 hover:bg-green-500 h-8" data-testid="save-cost-btn">
                            <Check className="w-3 h-3" />
                          </Button>
                          <Button size="sm" variant="ghost" onClick={() => { setEditingCost(false); setCostInput(String(finance.cost_per_audit)); }} className="h-8 text-zinc-400">
                            <XCircle className="w-3 h-3" />
                          </Button>
                        </div>
                      ) : (
                        <Button variant="outline" size="sm" onClick={() => setEditingCost(true)} className="border-white/10 text-white hover:bg-white/5" data-testid="edit-cost-btn">
                          £{finance.cost_per_audit.toFixed(2)} <Settings className="w-3 h-3 ml-2" />
                        </Button>
                      )}
                    </div>

                    {/* Cost statistics */}
                    <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                      <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                        <p className="text-zinc-500 text-xs">Audits Completed</p>
                        <p className="text-lg font-bold text-white">{finance.completed_audits}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-white/[0.02] border border-white/5">
                        <p className="text-zinc-500 text-xs">Audits This Month</p>
                        <p className="text-lg font-bold text-white">{finance.audits_this_month}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                        <p className="text-zinc-500 text-xs">Total API Cost</p>
                        <p className="text-lg font-bold text-red-400">£{finance.total_api_cost.toFixed(2)}</p>
                      </div>
                      <div className="p-3 rounded-lg bg-red-500/5 border border-red-500/10">
                        <p className="text-zinc-500 text-xs">Monthly API Cost</p>
                        <p className="text-lg font-bold text-red-400">£{finance.monthly_api_cost.toFixed(2)}</p>
                      </div>
                    </div>
                  </CardContent>
                </Card>

                {/* Net Profitability */}
                <Card className="bg-[#121212] border-white/10">
                  <CardHeader className="pb-2">
                    <CardTitle className="text-white flex items-center gap-2 text-base">
                      <TrendingUp className="w-5 h-5 text-emerald-400" /> Net Profitability
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <div className="grid grid-cols-2 gap-3">
                      <div className={`p-4 rounded-lg border ${finance.net_revenue >= 0 ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-red-500/5 border-red-500/10'}`}>
                        <p className="text-zinc-500 text-xs">Net Revenue (All Time)</p>
                        <p className={`text-2xl font-bold ${finance.net_revenue >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="finance-net-revenue">
                          £{finance.net_revenue.toFixed(2)}
                        </p>
                        <p className="text-xs text-zinc-600 mt-1">Revenue £{finance.total_revenue.toFixed(2)} - Cost £{finance.total_api_cost.toFixed(2)}</p>
                      </div>
                      <div className={`p-4 rounded-lg border ${finance.net_monthly >= 0 ? 'bg-emerald-500/5 border-emerald-500/10' : 'bg-red-500/5 border-red-500/10'}`}>
                        <p className="text-zinc-500 text-xs">Net This Month</p>
                        <p className={`text-2xl font-bold ${finance.net_monthly >= 0 ? 'text-emerald-400' : 'text-red-400'}`} data-testid="finance-net-monthly">
                          £{finance.net_monthly.toFixed(2)}
                        </p>
                        <p className="text-xs text-zinc-600 mt-1">Revenue £{finance.revenue_this_month.toFixed(2)} - Cost £{finance.monthly_api_cost.toFixed(2)}</p>
                      </div>
                    </div>
                    {finance.completed_audits > 0 && (
                      <div className="mt-4 p-3 rounded-lg bg-white/[0.02] border border-white/5">
                        <div className="flex flex-wrap gap-x-6 gap-y-2 text-sm">
                          <div>
                            <span className="text-zinc-500">Avg Revenue / Audit:</span>
                            <span className="text-white ml-2 font-medium">£{(finance.total_revenue / finance.completed_audits).toFixed(2)}</span>
                          </div>
                          <div>
                            <span className="text-zinc-500">Avg Profit / Audit:</span>
                            <span className={`ml-2 font-medium ${finance.net_revenue >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              £{(finance.net_revenue / finance.completed_audits).toFixed(2)}
                            </span>
                          </div>
                          <div>
                            <span className="text-zinc-500">Margin:</span>
                            <span className={`ml-2 font-medium ${finance.net_revenue >= 0 ? 'text-emerald-400' : 'text-red-400'}`}>
                              {finance.total_revenue > 0 ? ((finance.net_revenue / finance.total_revenue) * 100).toFixed(1) : '0'}%
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </CardContent>
                </Card>
              </>
            ) : null}
          </div>
        )}

        {/* Recent Audits - only on Overview */}
        {activeTab === 'overview' && (
        <Card className="bg-[#121212] border-white/10">
          <CardHeader>
            <CardTitle className="text-white flex items-center gap-2">
              <BarChart2 className="w-5 h-5" /> Recent Audits
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="md:hidden space-y-3">
              {stats?.recent_audits?.map(a => (
                <div key={a.id} className="rounded-lg border border-white/10 bg-[#1A1A1A] p-3">
                  <div className="flex items-start justify-between gap-3">
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-white font-medium break-all [overflow-wrap:anywhere]">{a.target_url}</div>
                      <div className="text-xs text-zinc-500 mt-1">{a.user_email || 'Anonymous'}</div>
                      <div className="mt-2 flex flex-wrap items-center gap-2">
                        <Badge variant="outline" className={a.report_mode === 'paid_full' ? 'border-green-500 text-green-400' : 'border-zinc-700 text-zinc-400'}>
                          {a.report_mode === 'paid_full' ? 'Full' : 'Baseline'}
                        </Badge>
                        <Badge className={
                          a.status === 'completed' ? 'bg-green-600' :
                          a.status === 'running' ? 'bg-blue-600' :
                          a.status === 'failed' ? 'bg-red-600' : 'bg-zinc-600'
                        }>
                          {a.status}
                        </Badge>
                      </div>
                    </div>
                    {a.status === 'completed' && (
                      <Link to={`/report/${a.id}`}>
                        <Button variant="ghost" size="sm" className="h-9 w-9 p-0">
                          <Eye className="w-4 h-4 text-blue-400" />
                        </Button>
                      </Link>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {/* Desktop Table */}
            <div className="hidden md:block">
            <Table>
              <TableHeader>
                <TableRow className="border-white/10">
                  <TableHead className="text-zinc-400">Domain</TableHead>
                  <TableHead className="text-zinc-400">User</TableHead>
                  <TableHead className="text-zinc-400">Type</TableHead>
                  <TableHead className="text-zinc-400">Status</TableHead>
                  <TableHead className="text-zinc-400">Date</TableHead>
                  <TableHead className="text-zinc-400 text-right">View</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {stats?.recent_audits?.map(a => (
                  <TableRow key={a.id} className="border-white/10">
                    <TableCell className="text-white font-medium max-w-[300px]">
                      <span className="break-all">{a.target_url}</span>
                    </TableCell>
                    <TableCell className="text-zinc-300">{a.user_email || 'Anonymous'}</TableCell>
                    <TableCell>
                      <Badge variant="outline" className={a.report_mode === 'paid_full' ? 'border-green-500 text-green-400' : 'border-zinc-700 text-zinc-400'}>
                        {a.report_mode === 'paid_full' ? 'Full' : 'Baseline'}
                      </Badge>
                    </TableCell>
                    <TableCell>
                      <Badge className={
                        a.status === 'completed' ? 'bg-green-600' :
                        a.status === 'running' ? 'bg-blue-600' :
                        a.status === 'failed' ? 'bg-red-600' : 'bg-zinc-600'
                      }>
                        {a.status}
                      </Badge>
                    </TableCell>
                    <TableCell className="text-zinc-400 text-sm">
                      {a.created_at ? new Date(a.created_at).toLocaleDateString() : '-'}
                    </TableCell>
                    <TableCell className="text-right">
                      {a.status === 'completed' && (
                        <Link to={`/report/${a.id}`}>
                          <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                            View
                          </Button>
                        </Link>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
            </div>
          </CardContent>
        </Card>
        )}

        {/* Audits Tab - Full List with Filters */}
        {activeTab === 'audits' && (
          <Card className="bg-[#121212] border-white/10">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <BarChart2 className="w-5 h-5" /> All Audits ({auditsTotal})
              </CardTitle>
            </CardHeader>
            <CardContent>
              {/* Search & Filters */}
              <div className="space-y-3 sm:space-y-0 sm:grid sm:grid-cols-2 lg:grid-cols-6 sm:gap-4 mb-4">
                <div className="lg:col-span-2">
                  <label className="text-zinc-500 text-xs block mb-1">Search (user, email, domain, audit ID)</label>
                  <Input
                    placeholder="Search audits..."
                    value={auditsFilter.search || ''}
                    onChange={(e) => handleAuditsFilterChange('search', e.target.value)}
                    className="bg-[#0A0A0A] border-white/10 text-white"
                  />
                </div>
                <div className="grid grid-cols-2 gap-3 sm:contents">
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">Date From</label>
                    <Input
                      type="date"
                      value={auditsFilter.dateFrom}
                      onChange={(e) => handleAuditsFilterChange('dateFrom', e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white h-9 text-sm w-full"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">Date To</label>
                    <Input
                      type="date"
                      value={auditsFilter.dateTo}
                      onChange={(e) => handleAuditsFilterChange('dateTo', e.target.value)}
                      className="bg-[#0A0A0A] border-white/10 text-white h-9 text-sm w-full"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-2 gap-3 sm:contents">
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">Status</label>
                    <Select value={auditsFilter.status || 'all'} onValueChange={(v) => handleAuditsFilterChange('status', v === 'all' ? '' : v)}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                        <SelectValue placeholder="All statuses" />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="all">All statuses</SelectItem>
                        <SelectItem value="completed">Completed</SelectItem>
                        <SelectItem value="running">Running</SelectItem>
                        <SelectItem value="failed">Failed</SelectItem>
                        <SelectItem value="pending">Pending</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button variant="outline" size="sm" onClick={clearAuditsFilters} className="border-white/10 text-zinc-400 w-full h-9">
                      Clear Filters
                    </Button>
                  </div>
                </div>
              </div>

              {/* Bulk Actions Bar */}
              <div className="flex flex-wrap items-center gap-2 mb-4 p-3 bg-[#0A0A0A] rounded-lg border border-white/10">
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={toggleSelectAllAudits}
                  className="text-zinc-400 hover:text-white text-xs h-8"
                >
                  {selectedAuditIds.length === allAudits.length && allAudits.length > 0 ? (
                    <CheckSquare className="w-3.5 h-3.5 mr-1.5" />
                  ) : (
                    <Square className="w-3.5 h-3.5 mr-1.5" />
                  )}
                  {selectedAuditIds.length > 0 ? `${selectedAuditIds.length}` : 'All'}
                </Button>
                
                {selectedAuditIds.length > 0 && (
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={handleBulkDeleteAudits}
                    disabled={deletingAudits}
                    className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs h-8"
                  >
                    <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                    Delete ({selectedAuditIds.length})
                  </Button>
                )}
                
                <div className="flex-1 min-w-0" />
                
                <Button
                  variant="outline"
                  size="sm"
                  onClick={handleDeleteFilteredAudits}
                  disabled={deletingAudits || auditsTotal === 0}
                  className="border-red-500/50 text-red-400 hover:bg-red-500/10 text-xs h-8 ml-auto"
                >
                  <Trash2 className="w-3.5 h-3.5 mr-1.5" />
                  Delete All ({auditsTotal})
                </Button>
              </div>

              {/* Audits Table */}
              {auditsLoading ? (
                <div className="space-y-2">
                  {[...Array(5)].map((_, i) => (
                    <Skeleton key={i} className="h-12 bg-[#1A1A1A]" />
                  ))}
                </div>
              ) : (
                <>
                  {/* Mobile Cards - hidden on desktop */}
                  <div className="md:hidden space-y-3">
                    {allAudits.length === 0 ? (
                      <p className="text-center text-zinc-500 py-8">No audits found</p>
                    ) : (
                      allAudits.map(a => (
                        <div key={a.id} className={`rounded-lg border border-white/10 bg-[#1A1A1A] p-3 ${a.is_locked ? 'bg-yellow-500/5' : ''}`}>
                          {/* Actions row at top */}
                          <div className="flex items-center justify-end gap-1 mb-2 pb-2 border-b border-white/5">
                            {/* Cancel Button for running audits */}
                            {['running', 'pending', 'crawling', 'analyzing', 'generating_report'].includes(a.status) && (
                              <Button variant="ghost" size="sm" onClick={() => handleCancelAudit(a.id)} className="h-8 w-8 p-0" title="Cancel audit">
                                <XCircle className="w-4 h-4 text-orange-400" />
                              </Button>
                            )}
                            
                            {/* Copy Links for completed */}
                            {a.status === 'completed' && (
                              <>
                                <Button variant="ghost" size="sm" onClick={() => handleCopyAuditLink(a, false)} className="h-8 w-8 p-0" title="Copy link">
                                  <Link2 className={`w-4 h-4 ${a.link_copied_at && !a.track_clicks ? 'text-green-400' : 'text-zinc-400'}`} />
                                </Button>
                                <Button 
                                  variant="ghost" 
                                  size="sm" 
                                  onClick={() => {
                                    if (a.track_clicks && a.link_clicked_at) {
                                      setClickDetailsAudit(a);
                                    } else {
                                      handleCopyAuditLink(a, true);
                                    }
                                  }} 
                                  className="h-8 w-8 p-0" 
                                  title={a.track_clicks && a.link_clicked_at ? "View click details" : "Copy with tracking"}
                                >
                                  {a.track_clicks && a.link_clicked_at ? (
                                    <Check className="w-4 h-4 text-green-400" />
                                  ) : (
                                    <Search className={`w-4 h-4 ${a.track_clicks ? 'text-blue-400' : 'text-zinc-400'}`} />
                                  )}
                                </Button>
                              </>
                            )}
                            
                            {/* Lock/Unlock */}
                            <Button variant="ghost" size="sm" onClick={() => handleToggleAuditLock(a.id, a.is_locked)} className="h-8 w-8 p-0" title={a.is_locked ? 'Unlock' : 'Lock'}>
                              {a.is_locked ? <Lock className="w-4 h-4 text-yellow-400" /> : <Unlock className="w-4 h-4 text-zinc-500" />}
                            </Button>
                            
                            {/* View */}
                            {a.status === 'completed' && (
                              <Link to={`/report/${a.id}`}>
                                <Button variant="ghost" size="sm" className="h-8 w-8 p-0" title="View report">
                                  <Eye className="w-4 h-4 text-blue-400" />
                                </Button>
                              </Link>
                            )}
                            
                            {/* Delete */}
                            <Button variant="ghost" size="sm" onClick={() => handleDeleteAudit(a.id)} disabled={a.is_locked} className="h-8 w-8 p-0" title={a.is_locked ? 'Unlock first' : 'Delete'}>
                              <Trash2 className={`w-4 h-4 ${a.is_locked ? 'text-zinc-600' : 'text-red-400'}`} />
                            </Button>
                          </div>
                          
                          {/* Audit data below */}
                          <div className="text-sm text-white font-medium break-all [overflow-wrap:anywhere]">
                            {a.target_url || a.report?.request?.target?.normalized_domain || '-'}
                          </div>
                          <div className="text-xs text-zinc-500 mt-1">{a.user_email || 'Anonymous'}</div>
                          <div className="mt-2 flex flex-wrap items-center gap-2">
                            <Badge variant="outline" className={a.report_mode === 'paid_full' ? 'border-green-500 text-green-400' : 'border-zinc-700 text-zinc-400'}>
                              {a.report_mode === 'paid_full' ? 'Full' : 'Free'}
                            </Badge>
                            <Badge className={
                              a.status === 'completed' ? 'bg-green-600' :
                              a.status === 'running' || a.status === 'crawling' || a.status === 'analyzing' || a.status === 'generating_report' ? 'bg-blue-600' :
                              a.status === 'failed' ? 'bg-red-600' :
                              a.status === 'cancelled' ? 'bg-orange-600' : 'bg-zinc-600'
                            }>
                              {a.status}
                            </Badge>
                            {a.is_locked && (
                              <Badge className="bg-yellow-600">Locked</Badge>
                            )}
                            <span className="text-xs text-zinc-500">
                              {a.created_at ? new Date(a.created_at).toLocaleDateString('pl-PL') : '-'}
                            </span>
                          </div>
                        </div>
                      ))
                    )}
                  </div>

                  {/* Desktop Table - hidden on mobile */}
                  <div className="hidden md:block">
                  <Table>
                    <TableHeader>
                      <TableRow className="border-white/10">
                        <TableHead className="text-zinc-400 w-10">
                          <button onClick={toggleSelectAllAudits} className="text-zinc-400 hover:text-white">
                            {selectedAuditIds.length === allAudits.length && allAudits.length > 0 ? (
                              <CheckSquare className="w-4 h-4" />
                            ) : (
                              <Square className="w-4 h-4" />
                            )}
                          </button>
                        </TableHead>
                        <TableHead className="text-zinc-400">ID</TableHead>
                        <TableHead className="text-zinc-400">Domain</TableHead>
                        <TableHead className="text-zinc-400">User</TableHead>
                        <TableHead className="text-zinc-400">Type</TableHead>
                        <TableHead className="text-zinc-400">Status</TableHead>
                        <TableHead className="text-zinc-400">Date</TableHead>
                        <TableHead className="text-zinc-400 text-right">Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {allAudits.length === 0 ? (
                        <TableRow className="border-white/10">
                          <TableCell colSpan={8} className="text-center text-zinc-500 py-8">
                            No audits found
                          </TableCell>
                        </TableRow>
                      ) : (
                        allAudits.map(a => (
                          <TableRow key={a.id} className={`border-white/10 ${a.is_locked ? 'bg-yellow-500/5' : ''}`}>
                            <TableCell>
                              <button 
                                onClick={() => toggleAuditSelection(a.id)}
                                className="text-zinc-400 hover:text-white"
                              >
                                {selectedAuditIds.includes(a.id) ? (
                                  <CheckSquare className="w-4 h-4 text-blue-400" />
                                ) : (
                                  <Square className="w-4 h-4" />
                                )}
                              </button>
                            </TableCell>
                            <TableCell className="text-zinc-500 font-mono text-xs">
                              <div className="flex items-center gap-1">
                                <span>{a.id?.substring(0, 8)}...</span>
                                <button 
                                  onClick={() => { navigator.clipboard.writeText(a.id); toast.success('Audit ID copied'); }}
                                  className="text-zinc-600 hover:text-zinc-300 transition-colors"
                                  title="Copy full ID"
                                >
                                  <Copy className="w-3 h-3" />
                                </button>
                              </div>
                            </TableCell>
                            <TableCell className="text-white font-medium max-w-[300px]">
                              <div className="flex items-center gap-2">
                                {a.is_locked && (
                                  <Lock className="w-3 h-3 text-yellow-400 flex-shrink-0" title={`Locked: ${a.lock_reason || 'Protected'}`} />
                                )}
                                <span className="break-all">{a.target_url || a.report?.request?.target?.normalized_domain || '-'}</span>
                              </div>
                            </TableCell>
                            <TableCell className="text-zinc-300">
                              {a.user_email || <span className="text-zinc-500 italic">Anonymous</span>}
                            </TableCell>
                            <TableCell>
                              <Badge variant="outline" className={a.report_mode === 'paid_full' ? 'border-green-500 text-green-400' : 'border-zinc-700 text-zinc-400'}>
                                {a.report_mode === 'paid_full' ? 'Full' : 'Free'}
                              </Badge>
                            </TableCell>
                            <TableCell>
                              <Badge className={
                                a.status === 'completed' ? 'bg-green-600' :
                                a.status === 'running' ? 'bg-blue-600' :
                                a.status === 'failed' ? 'bg-red-600' : 'bg-zinc-600'
                              }>
                                {a.status}
                              </Badge>
                            </TableCell>
                            <TableCell className="text-zinc-400 text-sm">
                              {a.created_at ? new Date(a.created_at).toLocaleString() : '-'}
                            </TableCell>
                            <TableCell className="text-right">
                              <div className="flex gap-1 justify-end">
                                {/* Cancel Button for running audits */}
                                {['running', 'pending', 'crawling', 'analyzing', 'generating_report'].includes(a.status) && (
                                  <Button
                                    variant="ghost"
                                    size="sm"
                                    onClick={() => handleCancelAudit(a.id)}
                                    className="text-orange-400 hover:text-orange-300 hover:bg-orange-500/10"
                                    title="Cancel audit"
                                  >
                                    <XCircle className="w-4 h-4" />
                                  </Button>
                                )}
                                
                                {/* Copy Link Button */}
                                {a.status === 'completed' && (
                                  <>
                                    {/* Copy link without tracking */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => handleCopyAuditLink(a, false)}
                                      className={`${a.link_copied_at && !a.track_clicks ? 'text-green-400' : 'text-zinc-400'} hover:text-green-300`}
                                      title={a.link_copied_at && !a.track_clicks ? `Link copied ${new Date(a.link_copied_at).toLocaleString()}` : 'Copy link (no tracking)'}
                                    >
                                      <Link2 className="w-4 h-4" />
                                    </Button>
                                    
                                    {/* Copy link with tracking */}
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => {
                                        if (a.track_clicks && a.link_clicked_at) {
                                          // Show click details modal
                                          setClickDetailsAudit(a);
                                        } else {
                                          // Copy tracking link
                                          handleCopyAuditLink(a, true);
                                        }
                                      }}
                                      className={`${a.track_clicks ? (a.link_clicked_at ? 'text-green-400' : 'text-blue-400') : 'text-zinc-400'} hover:text-blue-300`}
                                      title={a.track_clicks 
                                        ? (a.link_clicked_at 
                                          ? 'View click details' 
                                          : 'Tracking - not clicked yet') 
                                        : 'Copy with tracking'}
                                    >
                                      {a.track_clicks && a.link_clicked_at ? (
                                        <Check className="w-4 h-4" />
                                      ) : (
                                        <Search className="w-4 h-4" />
                                      )}
                                    </Button>
                                  </>
                                )}
                                
                                {/* Lock/Unlock Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleToggleAuditLock(a.id, a.is_locked)}
                                  className={`${a.is_locked ? 'text-yellow-400 hover:text-yellow-300' : 'text-zinc-500 hover:text-zinc-300'}`}
                                  title={a.is_locked ? 'Unlock audit' : 'Lock audit'}
                                >
                                  {a.is_locked ? <Lock className="w-4 h-4" /> : <Unlock className="w-4 h-4" />}
                                </Button>
                                
                                {/* View Button */}
                                {a.status === 'completed' && (
                                  <Link to={`/report/${a.id}`}>
                                    <Button variant="ghost" size="sm" className="text-blue-400 hover:text-blue-300">
                                      <Eye className="w-4 h-4" />
                                    </Button>
                                  </Link>
                                )}
                                
                                {/* Delete Button */}
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => handleDeleteAudit(a.id)}
                                  disabled={a.is_locked}
                                  className={`${a.is_locked ? 'text-zinc-600 cursor-not-allowed' : 'text-red-400 hover:text-red-300 hover:bg-red-500/10'}`}
                                  title={a.is_locked ? 'Unlock first to delete' : 'Delete audit'}
                                >
                                  <Trash2 className="w-4 h-4" />
                                </Button>
                              </div>
                            </TableCell>
                          </TableRow>
                        ))
                      )}
                    </TableBody>
                  </Table>
                  </div>

                  {/* Pagination - only show when filters are active and there are more than 50 audits */}
                  {(auditsFilter.search || auditsFilter.dateFrom || auditsFilter.dateTo || auditsFilter.status) && auditsTotal > 50 && (
                    <div className="flex justify-between items-center mt-4">
                      <p className="text-zinc-500 text-sm">
                        Showing {auditsPage * 50 + 1} - {Math.min((auditsPage + 1) * 50, auditsTotal)} of {auditsTotal}
                      </p>
                      <div className="flex gap-2">
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchAudits(auditsPage - 1, auditsFilter)}
                          disabled={auditsPage === 0}
                          className="border-white/10 text-zinc-400"
                        >
                          Previous
                        </Button>
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => fetchAudits(auditsPage + 1, auditsFilter)}
                          disabled={(auditsPage + 1) * 50 >= auditsTotal}
                          className="border-white/10 text-zinc-400"
                        >
                          Next
                        </Button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </CardContent>
          </Card>
        )}

        {/* Messages Tab */}
        {activeTab === 'messages' && (
          <div className="space-y-6">
            {/* Contact Messages */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <MessageSquare className="w-5 h-5" /> Contact Messages ({messages.length})
                  {unreadContactCount > 0 && (
                    <span className="ml-2 bg-yellow-500 text-black text-xs px-2 py-0.5 rounded-full">{unreadContactCount} new</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {messagesLoading ? (
                  <div className="space-y-2">
                    {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-12 bg-[#1A1A1A]" />)}
                  </div>
                ) : messages.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No messages yet</p>
                ) : (
                  <div className="space-y-2">
                    {messages.map(msg => (
                      <div 
                        key={msg.id} 
                        className={`rounded-lg border transition-all ${!msg.read ? 'bg-yellow-500/10 border-yellow-500/30' : msg.status === 'replied' ? 'bg-green-500/5 border-green-500/20' : 'bg-[#0D0D0D] border-white/10'}`}
                      >
                        {/* Compact header - always visible */}
                        <div 
                          className="p-3 flex items-center justify-between cursor-pointer hover:bg-white/5"
                          onClick={() => toggleExpandMessage(msg.id)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {!msg.read && <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-white text-sm font-medium truncate">{msg.name}</span>
                                <span className="text-blue-400 text-xs truncate hidden sm:inline">{msg.email}</span>
                                <span className="text-zinc-600 text-xs hidden sm:inline">•</span>
                                <span className="text-zinc-500 text-xs hidden sm:inline">{new Date(msg.created_at).toLocaleString()}</span>
                              </div>
                              <p className="text-zinc-400 text-sm truncate">{msg.message?.substring(0, 80)}{msg.message?.length > 80 ? '...' : ''}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            {msg.status === 'replied' && <Badge className="bg-green-600 text-xs">Replied</Badge>}
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleDeleteMessage(msg.id); }}
                              className="text-red-400 hover:text-red-300 h-9 w-9 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${expandedMessageId === msg.id ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded content */}
                        {expandedMessageId === msg.id && (
                          <div className="px-3 pb-3 border-t border-white/10">
                            <div className="pt-3">
                              <div className="flex items-center gap-2 mb-2 text-sm">
                                <span className="text-zinc-500">From:</span>
                                <span className="text-blue-400">{msg.email}</span>
                                <span className="text-zinc-600">•</span>
                                <span className="text-zinc-500">{new Date(msg.created_at).toLocaleString()}</span>
                              </div>
                              <p className="text-zinc-300 whitespace-pre-wrap text-sm mb-3">{msg.message}</p>
                              
                              {/* Replies */}
                              {msg.replies?.length > 0 && (
                                <div className="border-t border-white/10 pt-3 mt-3 space-y-2">
                                  {msg.replies.map((reply, i) => (
                                    <div key={i} className="bg-[#1A1A1A] p-3 rounded-lg">
                                      <p className="text-zinc-400 text-sm">{reply.reply_text}</p>
                                      <p className="text-zinc-600 text-xs mt-1">Replied by {reply.replied_by} • {new Date(reply.replied_at).toLocaleString()}</p>
                                    </div>
                                  ))}
                                </div>
                              )}
                              
                              {/* Reply Form */}
                              {replyingTo === msg.id ? (
                                <div className="border-t border-white/10 pt-3 mt-3 space-y-2">
                                  <textarea
                                    value={replyText}
                                    onChange={(e) => setReplyText(e.target.value)}
                                    placeholder="Type your reply..."
                                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg p-3 text-white resize-none"
                                    rows={3}
                                  />
                                  <div className="flex gap-2">
                                    <Button size="sm" onClick={() => handleReplyMessage(msg.id)} className="bg-blue-600">
                                      <Send className="w-4 h-4 mr-1" /> Send Reply
                                    </Button>
                                    <Button size="sm" variant="ghost" onClick={() => { setReplyingTo(null); setReplyText(''); }}>
                                      Cancel
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <div className="flex gap-2 mt-3">
                                  <Button size="sm" variant="outline" onClick={(e) => { e.stopPropagation(); setReplyingTo(msg.id); }} className="border-white/10">
                                    <Mail className="w-4 h-4 mr-1" /> Reply
                                  </Button>
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Inbound Emails (Customer Replies) */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Mail className="w-5 h-5" /> Customer Replies ({inboundEmails.length})
                  {unreadInboundCount > 0 && (
                    <span className="ml-2 bg-yellow-500 text-black text-xs px-2 py-0.5 rounded-full">{unreadInboundCount} new</span>
                  )}
                </CardTitle>
              </CardHeader>
              <CardContent>
                {inboundEmails.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No customer replies yet</p>
                ) : (
                  <div className="space-y-2">
                    {inboundEmails.map(email => (
                      <div 
                        key={email.id} 
                        className={`rounded-lg border transition-all ${!email.read ? 'bg-yellow-500/10 border-yellow-500/30' : 'bg-[#0D0D0D] border-white/10'}`}
                      >
                        {/* Compact header - always visible */}
                        <div 
                          className="p-3 flex items-center justify-between cursor-pointer hover:bg-white/5"
                          onClick={() => toggleExpandInbound(email.id)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            {!email.read && <div className="w-2 h-2 rounded-full bg-yellow-500 flex-shrink-0" />}
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-blue-400 text-sm font-medium truncate">{email.from_email}</span>
                                <span className="text-zinc-600 text-xs hidden sm:inline">•</span>
                                <span className="text-zinc-500 text-xs hidden sm:inline">{new Date(email.created_at).toLocaleString()}</span>
                              </div>
                              <p className="text-white text-sm truncate">{email.subject || '(No subject)'}</p>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); handleDeleteInboundEmail(email.id); }}
                              className="text-red-400 hover:text-red-300 h-9 w-9 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${expandedInboundId === email.id ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded content */}
                        {expandedInboundId === email.id && (
                          <div className="px-3 pb-3 border-t border-white/10">
                            <div className="pt-3">
                              <p className="text-zinc-300 whitespace-pre-wrap text-sm">{email.text || email.html?.replace(/<[^>]*>/g, '') || '(No text content)'}</p>
                              {email.linked_message_id && (
                                <p className="text-zinc-600 text-xs mt-2">Linked to contact message</p>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Bulk Email Sender */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Send className="w-5 h-5" /> Send Bulk Email
                  <span className="text-xs text-zinc-500 font-normal ml-2">(max 50 recipients)</span>
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">Audience</label>
                    <Select value={bulkEmail.audience} onValueChange={handleAudienceChange}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#121212] border-white/10">
                        <SelectItem value="all">All Users</SelectItem>
                        <SelectItem value="active">Active Subscribers</SelectItem>
                        <SelectItem value="inactive">Inactive Users</SelectItem>
                        <SelectItem value="free">Free Tier Only</SelectItem>
                        <SelectItem value="core">Core Tier Only</SelectItem>
                        <SelectItem value="pro">Pro Tier Only</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="relative">
                    <label className="text-zinc-500 text-xs block mb-1">Add recipient (search or type email)</label>
                    <div className="flex gap-2">
                      <div className="relative flex-1">
                        <Input
                          value={customEmail}
                          onChange={handleCustomEmailChange}
                          onFocus={() => customEmail.length >= 2 && searchUsers(customEmail)}
                          onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
                          placeholder="Start typing email or name..."
                          className="bg-[#0A0A0A] border-white/10 text-white"
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              if (userSuggestions.length > 0) {
                                addUserFromSuggestion(userSuggestions[0]);
                              } else {
                                addCustomEmail();
                              }
                            }
                          }}
                          data-testid="recipient-search-input"
                        />
                        {/* Autocomplete dropdown */}
                        {showSuggestions && userSuggestions.length > 0 && (
                          <div className="absolute top-full left-0 right-0 mt-1 bg-[#1A1A1A] border border-white/10 rounded-lg shadow-xl z-50 max-h-48 overflow-y-auto">
                            {userSuggestions.map((user) => (
                              <div
                                key={user.email}
                                className="px-3 py-2 hover:bg-white/5 cursor-pointer flex items-center justify-between"
                                onMouseDown={() => addUserFromSuggestion(user)}
                                data-testid={`suggestion-${user.email}`}
                              >
                                <div>
                                  <p className="text-white text-sm">{user.email}</p>
                                  {user.name && <p className="text-zinc-500 text-xs">{user.name}</p>}
                                </div>
                                <Badge variant="outline" className={`text-[10px] ${
                                  user.tier === 'pro' ? 'border-purple-500 text-purple-400' :
                                  user.tier === 'entry' ? 'border-blue-500 text-blue-400' :
                                  'border-zinc-600 text-zinc-500'
                                }`}>{user.tier}</Badge>
                              </div>
                            ))}
                          </div>
                        )}
                        {loadingSuggestions && (
                          <div className="absolute right-3 top-1/2 -translate-y-1/2">
                            <div className="w-4 h-4 border-2 border-zinc-500 border-t-transparent rounded-full animate-spin"></div>
                          </div>
                        )}
                      </div>
                      <Button variant="outline" onClick={addCustomEmail} className="border-white/10" title="Add custom email">
                        <Plus className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                </div>

                {/* Recipients Preview */}
                {(previewRecipients.length > 0 || loadingRecipients) && (
                  <div className="border border-white/10 rounded-lg p-4 bg-[#0D0D0D]">
                    <div className="flex justify-between items-center mb-3">
                      <label className="text-zinc-500 text-xs">
                        Recipients ({selectedRecipients.length} selected of {previewRecipients.length})
                        {selectedRecipients.length > 50 && <span className="text-yellow-500 ml-2">⚠ Only first 50 will be sent</span>}
                      </label>
                      <div className="flex gap-2">
                        <Button variant="ghost" size="sm" onClick={() => setSelectedRecipients(previewRecipients.map(r => r.email))} className="text-xs text-zinc-400">
                          Select All
                        </Button>
                        <Button variant="ghost" size="sm" onClick={() => setSelectedRecipients([])} className="text-xs text-zinc-400">
                          Clear
                        </Button>
                      </div>
                    </div>
                    {loadingRecipients ? (
                      <div className="flex gap-2">
                        {[...Array(3)].map((_, i) => <Skeleton key={i} className="h-8 w-32 bg-[#1A1A1A]" />)}
                      </div>
                    ) : (
                      <div className="flex flex-wrap gap-2 max-h-40 overflow-y-auto">
                        {previewRecipients.map(r => (
                          <div
                            key={r.email}
                            onClick={() => toggleRecipient(r.email)}
                            className={`flex items-center gap-2 px-3 py-1.5 rounded-full text-sm cursor-pointer transition-all ${
                              selectedRecipients.includes(r.email)
                                ? 'bg-blue-600/20 border border-blue-500/50 text-blue-300'
                                : 'bg-[#1A1A1A] border border-white/10 text-zinc-500'
                            }`}
                          >
                            <span className={selectedRecipients.includes(r.email) ? 'opacity-100' : 'opacity-50'}>
                              {r.email}
                            </span>
                            {r.tier && r.tier !== 'custom' && (
                              <Badge variant="outline" className={`text-[10px] ${
                                r.tier === 'pro' ? 'border-purple-500 text-purple-400' :
                                r.tier === 'entry' ? 'border-blue-500 text-blue-400' :
                                'border-zinc-600 text-zinc-500'
                              }`}>{r.tier}</Badge>
                            )}
                            {r.tier === 'custom' && <Badge variant="outline" className="text-[10px] border-green-500 text-green-400">custom</Badge>}
                          </div>
                        ))}
                      </div>
                    )}
                  </div>
                )}

                <div>
                  <label className="text-zinc-500 text-xs block mb-1">Subject *</label>
                  <Input
                    value={bulkEmail.subject}
                    onChange={(e) => setBulkEmail({...bulkEmail, subject: e.target.value})}
                    placeholder="Email subject line"
                    className="bg-[#0A0A0A] border-white/10 text-white"
                  />
                </div>
                <div>
                  <label className="text-zinc-500 text-xs block mb-1">Message *</label>
                  <textarea
                    value={bulkEmail.body}
                    onChange={(e) => setBulkEmail({...bulkEmail, body: e.target.value})}
                    placeholder="Email body content..."
                    className="w-full bg-[#0A0A0A] border border-white/10 rounded-lg p-3 text-white resize-none"
                    rows={5}
                  />
                </div>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">CTA Button Text (optional)</label>
                    <Input
                      value={bulkEmail.cta_text}
                      onChange={(e) => setBulkEmail({...bulkEmail, cta_text: e.target.value})}
                      placeholder="e.g. View Pricing"
                      className="bg-[#0A0A0A] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-500 text-xs block mb-1">CTA Button URL (optional)</label>
                    <div className="flex gap-2">
                      <Input
                        value={bulkEmail.cta_url}
                        onChange={(e) => setBulkEmail({...bulkEmail, cta_url: e.target.value})}
                        placeholder="https://scorecite.io/pricing"
                        className="bg-[#0A0A0A] border-white/10 text-white flex-1"
                      />
                      <label className="flex items-center gap-2 text-zinc-400 text-xs cursor-pointer px-3 py-2 rounded border border-white/10 hover:bg-white/5">
                        <input
                          type="checkbox"
                          checked={bulkEmail.track_clicks}
                          onChange={(e) => setBulkEmail({...bulkEmail, track_clicks: e.target.checked})}
                          className="w-4 h-4 accent-blue-500"
                          disabled={!bulkEmail.cta_url}
                        />
                        Track clicks
                      </label>
                    </div>
                  </div>
                </div>
                <Button 
                  onClick={handleSendBulkEmail} 
                  disabled={sendingBulk || !bulkEmail.subject || !bulkEmail.body || selectedRecipients.length === 0}
                  className="bg-blue-600"
                >
                  {sendingBulk ? 'Sending...' : `Send Email to ${selectedRecipients.length} recipient(s)`}
                </Button>
              </CardContent>
            </Card>

            {/* Sent Emails History */}
            {emailCampaigns.length > 0 && (
              <Card className="bg-[#121212] border-white/10">
                <CardHeader>
                  <CardTitle className="text-white text-lg flex items-center gap-2">
                    <Send className="w-5 h-5" /> Sent Emails ({emailCampaigns.length})
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-2">
                    {emailCampaigns.slice(0, 10).map(c => (
                      <div 
                        key={c.id} 
                        className="rounded-lg border bg-[#0D0D0D] border-white/10 transition-all"
                      >
                        {/* Compact header - always visible */}
                        <div 
                          className="p-3 flex items-center justify-between cursor-pointer hover:bg-white/5"
                          onClick={() => setExpandedCampaignId(expandedCampaignId === c.id ? null : c.id)}
                        >
                          <div className="flex items-center gap-3 flex-1 min-w-0">
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center gap-2">
                                <span className="text-white text-sm font-medium truncate">{c.subject}</span>
                                <Badge className={c.sent_count > 0 ? "bg-green-600/20 text-green-400 text-xs" : "bg-red-600/20 text-red-400 text-xs"}>
                                  {c.sent_count}/{c.recipients_count || '?'} sent
                                </Badge>
                                {/* Click tracking icons */}
                                {c.track_clicks && (
                                  <div className="flex items-center gap-1" title={c.click_count > 0 ? `${c.click_count} click(s)` : 'Tracking enabled, no clicks yet'}>
                                    <Search className="w-3.5 h-3.5 text-blue-400" />
                                    {c.click_count > 0 && (
                                      <span className="flex items-center text-green-400">
                                        <Check className="w-3.5 h-3.5" />
                                        <span className="text-xs ml-0.5">{c.click_count}</span>
                                      </span>
                                    )}
                                  </div>
                                )}
                              </div>
                              <div className="flex items-center gap-2 text-xs text-zinc-500">
                                {/* Show recipients instead of just "custom" */}
                                {c.recipients_list && c.recipients_list.length > 0 ? (
                                  <span className="text-zinc-400 truncate max-w-[300px]" title={c.recipients_list.join(', ')}>
                                    {c.recipients_list.slice(0, 2).join(', ')}{c.recipients_list.length > 2 && ` +${c.recipients_list.length - 2}`}
                                  </span>
                                ) : (
                                  <span>{c.audience}</span>
                                )}
                                <span>•</span>
                                <span>{new Date(c.sent_at).toLocaleString()}</span>
                              </div>
                            </div>
                          </div>
                          <div className="flex items-center gap-2 flex-shrink-0 ml-2">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={(e) => { e.stopPropagation(); deleteCampaign(c.id); }}
                              className="text-red-400 hover:text-red-300 h-9 w-9 p-0"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                            <ChevronDown className={`w-4 h-4 text-zinc-500 transition-transform ${expandedCampaignId === c.id ? 'rotate-180' : ''}`} />
                          </div>
                        </div>
                        
                        {/* Expanded content */}
                        {expandedCampaignId === c.id && (
                          <div className="px-3 pb-3 border-t border-white/10">
                            <div className="pt-3">
                              {/* Stats */}
                              <div className={`grid ${c.track_clicks ? 'grid-cols-4' : 'grid-cols-3'} gap-2 mb-3`}>
                                <div className="bg-[#1A1A1A] rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-white">{c.recipients_count || 0}</p>
                                  <p className="text-zinc-500 text-xs">Recipients</p>
                                </div>
                                <div className="bg-[#1A1A1A] rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-green-400">{c.sent_count || 0}</p>
                                  <p className="text-zinc-500 text-xs">Sent</p>
                                </div>
                                <div className="bg-[#1A1A1A] rounded-lg p-2 text-center">
                                  <p className="text-lg font-bold text-red-400">{c.failed_count || 0}</p>
                                  <p className="text-zinc-500 text-xs">Failed</p>
                                </div>
                                {c.track_clicks && (
                                  <div className="bg-[#1A1A1A] rounded-lg p-2 text-center">
                                    <p className="text-lg font-bold text-blue-400">{c.click_count || 0}</p>
                                    <p className="text-zinc-500 text-xs">Clicks</p>
                                  </div>
                                )}
                              </div>
                              
                              {/* Click details */}
                              {c.track_clicks && c.clicks && c.clicks.length > 0 && (
                                <div className="bg-blue-500/10 border border-blue-500/20 rounded-lg p-3 mb-3">
                                  <p className="text-blue-400 text-xs font-semibold mb-2">Link Clicked By:</p>
                                  <div className="space-y-1">
                                    {c.clicks.slice(0, 5).map((click, i) => (
                                      <div key={i} className="flex items-center justify-between text-xs">
                                        <span className="text-zinc-300">{click.email || 'Unknown'}</span>
                                        <span className="text-zinc-500">{new Date(click.clicked_at).toLocaleString()}</span>
                                      </div>
                                    ))}
                                    {c.clicks.length > 5 && (
                                      <p className="text-zinc-500 text-xs">+{c.clicks.length - 5} more clicks</p>
                                    )}
                                  </div>
                                </div>
                              )}
                              
                              {/* Message Content */}
                              <div className="bg-[#1A1A1A] border border-white/10 rounded-lg p-3">
                                <p className="text-zinc-300 whitespace-pre-wrap text-sm">{c.body}</p>
                                {c.cta_text && (
                                  <div className="mt-2 pt-2 border-t border-white/10 flex items-center gap-2">
                                    <span className="text-zinc-500 text-xs">CTA: </span>
                                    <a href={c.cta_url} target="_blank" rel="noopener noreferrer" className="text-blue-400 text-sm underline">
                                      {c.cta_text}
                                    </a>
                                    {c.track_clicks && <Search className="w-3.5 h-3.5 text-blue-400" title="Click tracking enabled" />}
                                  </div>
                                )}
                              </div>
                              
                              {/* Recipients list if available */}
                              {c.recipients_list && c.recipients_list.length > 0 && (
                                <div className="mt-3 space-y-3">
                                  {/* Sent emails - green */}
                                  {c.sent_emails && c.sent_emails.length > 0 && (
                                    <div>
                                      <p className="text-green-400 text-xs font-semibold mb-1 flex items-center gap-1">
                                        <span className="w-2 h-2 bg-green-400 rounded-full"></span>
                                        Sent successfully ({c.sent_emails.length}):
                                      </p>
                                      <div className="flex flex-wrap gap-1">
                                        {c.sent_emails.slice(0, 15).map((r, i) => (
                                          <span key={i} className="text-xs bg-green-500/10 border border-green-500/20 px-2 py-1 rounded text-green-400">{r}</span>
                                        ))}
                                        {c.sent_emails.length > 15 && (
                                          <span className="text-xs text-zinc-500">+{c.sent_emails.length - 15} more</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Failed emails - red */}
                                  {c.failed_emails && c.failed_emails.length > 0 && (
                                    <div>
                                      <p className="text-red-400 text-xs font-semibold mb-1 flex items-center gap-1">
                                        <span className="w-2 h-2 bg-red-400 rounded-full"></span>
                                        Failed to send ({c.failed_emails.length}):
                                      </p>
                                      <div className="flex flex-wrap gap-1">
                                        {c.failed_emails.slice(0, 15).map((item, i) => {
                                          const email = typeof item === 'string' ? item : item.email;
                                          const error = typeof item === 'object' ? item.error : null;
                                          return (
                                            <span 
                                              key={i} 
                                              className="text-xs bg-red-500/10 border border-red-500/20 px-2 py-1 rounded text-red-400 cursor-help"
                                              title={error || 'Unknown error'}
                                            >
                                              {email}
                                            </span>
                                          );
                                        })}
                                        {c.failed_emails.length > 15 && (
                                          <span className="text-xs text-zinc-500">+{c.failed_emails.length - 15} more</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                  
                                  {/* Show old format recipients if sent_emails not available */}
                                  {!c.sent_emails && (
                                    <div>
                                      <p className="text-zinc-500 text-xs mb-1">All Recipients:</p>
                                      <div className="flex flex-wrap gap-1">
                                        {c.recipients_list.slice(0, 10).map((r, i) => (
                                          <span key={i} className="text-xs bg-[#1A1A1A] px-2 py-1 rounded text-zinc-400">{r}</span>
                                        ))}
                                        {c.recipients_list.length > 10 && (
                                          <span className="text-xs text-zinc-500">+{c.recipients_list.length - 10} more</span>
                                        )}
                                      </div>
                                    </div>
                                  )}
                                </div>
                              )}
                            </div>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>
            )}
          </div>
        )}

        {/* User Details Modal */}
        {selectedUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setSelectedUser(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-2xl w-full max-h-[80vh] overflow-auto" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-white">User Details: {selectedUser.user?.email}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Pending Payment Warning */}
                {selectedUser.user?.subscription?.subscription_status === 'pending_payment' && (
                  <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                    <div className="flex items-center gap-2 text-yellow-400 font-semibold mb-2">
                      <AlertTriangle className="w-5 h-5" />
                      Pending Payment - Quote Sent
                    </div>
                    <p className="text-yellow-200 text-sm">
                      This user has been set up with an Enterprise quote but has NOT paid yet.
                      They will not receive credits until payment is completed.
                    </p>
                    {selectedUser.user?.subscription?.enterprise_config && (
                      <div className="mt-3 p-3 bg-black/30 rounded">
                        <p className="text-zinc-400 text-xs uppercase tracking-wider mb-2">Quote Details</p>
                        <div className="grid grid-cols-3 gap-4">
                          <div>
                            <p className="text-zinc-500 text-xs">Price</p>
                            <p className="text-white font-bold">£{selectedUser.user.subscription.enterprise_config.custom_price}/mo</p>
                          </div>
                          <div>
                            <p className="text-zinc-500 text-xs">Credits</p>
                            <p className="text-white font-bold">{selectedUser.user.subscription.enterprise_config.custom_credits}/mo</p>
                          </div>
                          <div>
                            <p className="text-zinc-500 text-xs">Domains</p>
                            <p className="text-white font-bold">{selectedUser.user.subscription.enterprise_config.custom_domains}</p>
                          </div>
                        </div>
                        {selectedUser.user.subscription.enterprise_config.first_month_discount > 0 && (
                          <div className="mt-2 pt-2 border-t border-white/10">
                            <p className="text-green-400 text-sm font-semibold">
                              🎉 {selectedUser.user.subscription.enterprise_config.first_month_discount}% off first month → 
                              £{Math.round(selectedUser.user.subscription.enterprise_config.custom_price * (1 - selectedUser.user.subscription.enterprise_config.first_month_discount / 100))} first month
                            </p>
                          </div>
                        )}
                      </div>
                    )}
                    <Button 
                      className="mt-3 bg-yellow-600 hover:bg-yellow-700 text-black font-semibold"
                      onClick={() => sendEnterpriseOffer({
                        email: selectedUser.user?.email,
                        name: selectedUser.user?.name,
                        subscription: {
                          ...selectedUser.user?.subscription,
                          enterprise_quote: {
                            price: selectedUser.user?.subscription?.enterprise_config?.custom_price,
                            credits: selectedUser.user?.subscription?.enterprise_config?.custom_credits,
                            domains: selectedUser.user?.subscription?.enterprise_config?.custom_domains,
                            first_month_discount: selectedUser.user?.subscription?.enterprise_config?.first_month_discount
                          }
                        }
                      })}
                    >
                      <Send className="w-4 h-4 mr-2" /> Send Offer Email
                    </Button>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <p className="text-zinc-500 text-sm">Name</p>
                    <p className="text-white">{selectedUser.user?.name || '-'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Plan</p>
                    <p className="text-white flex items-center gap-2">
                      <Badge className={
                        selectedUser.user?.subscription?.tier === 'enterprise' ? 'bg-purple-600' :
                        selectedUser.user?.subscription?.tier === 'pro' ? 'bg-blue-600' :
                        selectedUser.user?.subscription?.tier === 'core' || selectedUser.user?.subscription?.tier === 'entry' ? 'bg-green-600' : 'bg-zinc-600'
                      }>
                        {selectedUser.user?.subscription?.tier || 'Free'}
                      </Badge>
                      {selectedUser.user?.subscription?.subscription_status === 'pending_payment' && (
                        <span className="text-yellow-400 text-xs">(UNPAID)</span>
                      )}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Subscription Status</p>
                    <p className={`font-medium ${
                      selectedUser.user?.subscription?.subscription_status === 'active' ? 'text-green-400' :
                      selectedUser.user?.subscription?.subscription_status === 'pending_payment' ? 'text-yellow-400' :
                      selectedUser.user?.subscription?.subscription_status === 'cancelled' ? 'text-orange-400' :
                      selectedUser.user?.subscription?.subscription_status === 'expired' ? 'text-red-400' :
                      'text-zinc-400'
                    }`}>
                      {selectedUser.user?.subscription?.subscription_status || 'None'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Audits Count</p>
                    <p className="text-white">{selectedUser.user?.audits_count || 0}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Credits Remaining</p>
                    <p className="text-white">{selectedUser.user?.is_admin ? '∞ Unlimited' : (selectedUser.user?.subscription?.credits_remaining || 0)}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Joined</p>
                    <p className="text-white">{selectedUser.user?.created_at ? new Date(selectedUser.user.created_at).toLocaleString() : '-'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Last Login</p>
                    <p className="text-white">{selectedUser.user?.last_login ? new Date(selectedUser.user.last_login).toLocaleString() : 'Never'}</p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Last Payment</p>
                    <p className="text-white">
                      {selectedUser.user?.subscription?.last_payment_date 
                        ? new Date(selectedUser.user.subscription.last_payment_date).toLocaleDateString('pl-PL')
                        : <span className="text-zinc-600">Never paid</span>}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Next Payment Due</p>
                    <p className="text-white">
                      {selectedUser.user?.subscription?.current_period_end ? (
                        <>
                          {new Date(selectedUser.user.subscription.current_period_end).toLocaleDateString('pl-PL')}
                          {(() => {
                            const endDate = new Date(selectedUser.user.subscription.current_period_end);
                            const now = new Date();
                            const daysLeft = Math.ceil((endDate - now) / (1000 * 60 * 60 * 24));
                            return (
                              <span className={`ml-2 ${daysLeft <= 3 ? 'text-red-400' : daysLeft <= 7 ? 'text-yellow-400' : 'text-green-400'}`}>
                                ({daysLeft} days left)
                              </span>
                            );
                          })()}
                        </>
                      ) : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Amount Paying</p>
                    <p className="text-white">
                      {selectedUser.user?.subscription?.amount_paid 
                        ? `£${selectedUser.user.subscription.amount_paid}/mo`
                        : selectedUser.user?.subscription?.enterprise_config?.custom_price
                          ? <span className="text-yellow-400">£{selectedUser.user.subscription.enterprise_config.custom_price}/mo (quote)</span>
                          : '-'}
                    </p>
                  </div>
                  <div>
                    <p className="text-zinc-500 text-sm">Stripe Customer ID</p>
                    <p className="text-white font-mono text-xs">
                      {selectedUser.user?.subscription?.customer_id || (
                        <span className="text-zinc-600">Not set</span>
                      )}
                    </p>
                  </div>
                </div>

                {selectedUser.audits?.length > 0 && (
                  <>
                    <h3 className="text-lg font-semibold text-white mt-6">User's Audits</h3>
                    <div className="space-y-2">
                      {selectedUser.audits.map(a => (
                        <div key={a.id} className="flex items-center justify-between p-3 bg-[#0D0D0D] rounded-sm border border-white/10">
                          <div>
                            <p className="text-white text-sm">{a.target_url}</p>
                            <p className="text-zinc-500 text-xs">{a.created_at ? new Date(a.created_at).toLocaleDateString() : ''}</p>
                          </div>
                          <Badge className={a.status === 'completed' ? 'bg-green-600' : 'bg-zinc-600'}>{a.status}</Badge>
                        </div>
                      ))}
                    </div>
                  </>
                )}

                <Button onClick={() => setSelectedUser(null)} className="w-full mt-4">Close</Button>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Discounts Tab */}
        {activeTab === 'discounts' && (
          <div className="space-y-6">
            {/* Create New Code */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Plus className="w-5 h-5" /> Create Discount Code
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                  <div>
                    <label className="text-zinc-400 text-sm block mb-1">Code *</label>
                    <Input
                      value={newCode.code}
                      onChange={(e) => setNewCode({...newCode, code: e.target.value.toUpperCase()})}
                      placeholder="e.g. WELCOME20"
                      className="bg-[#0A0A0A] border-white/10 text-white uppercase"
                      data-testid="discount-code-input"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm block mb-1">Type *</label>
                    <Select value={newCode.discount_type} onValueChange={(v) => setNewCode({...newCode, discount_type: v})}>
                      <SelectTrigger className="bg-[#0A0A0A] border-white/10 text-white">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-[#1A1A1A] border-white/10">
                        <SelectItem value="percent">Percentage (%)</SelectItem>
                        <SelectItem value="fixed">Fixed Amount (£)</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm block mb-1">
                      Value * {newCode.discount_type === 'percent' ? '(%)' : '(£)'}
                    </label>
                    <Input
                      type="number"
                      value={newCode.discount_value}
                      onChange={(e) => setNewCode({...newCode, discount_value: e.target.value})}
                      placeholder={newCode.discount_type === 'percent' ? '20' : '5'}
                      className="bg-[#0A0A0A] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm block mb-1">Max Uses (empty = unlimited)</label>
                    <Input
                      type="number"
                      value={newCode.max_uses}
                      onChange={(e) => setNewCode({...newCode, max_uses: e.target.value})}
                      placeholder="10"
                      className="bg-[#0A0A0A] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm block mb-1">Expires At (empty = never)</label>
                    <Input
                      type="datetime-local"
                      value={newCode.expires_at}
                      onChange={(e) => setNewCode({...newCode, expires_at: e.target.value})}
                      className="bg-[#0A0A0A] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm block mb-1">Valid For Plans</label>
                    <div className="flex gap-4 mt-2">
                      <label className="flex items-center gap-2 text-zinc-300 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newCode.valid_for_tiers.includes('core')}
                          onChange={(e) => {
                            const tiers = e.target.checked 
                              ? [...newCode.valid_for_tiers, 'core']
                              : newCode.valid_for_tiers.filter(t => t !== 'core');
                            setNewCode({...newCode, valid_for_tiers: tiers});
                          }}
                          className="rounded border-white/20"
                        />
                        Core
                      </label>
                      <label className="flex items-center gap-2 text-zinc-300 text-sm cursor-pointer">
                        <input
                          type="checkbox"
                          checked={newCode.valid_for_tiers.includes('pro')}
                          onChange={(e) => {
                            const tiers = e.target.checked 
                              ? [...newCode.valid_for_tiers, 'pro']
                              : newCode.valid_for_tiers.filter(t => t !== 'pro');
                            setNewCode({...newCode, valid_for_tiers: tiers});
                          }}
                          className="rounded border-white/20"
                        />
                        Pro
                      </label>
                    </div>
                  </div>
                </div>
                <div className="flex flex-col sm:flex-row gap-4">
                  <div className="flex-1">
                    <label className="text-zinc-400 text-sm block mb-1">Description (optional)</label>
                    <Input
                      value={newCode.description}
                      onChange={(e) => setNewCode({...newCode, description: e.target.value})}
                      placeholder="e.g. Launch week promotion"
                      className="bg-[#0A0A0A] border-white/10 text-white"
                    />
                  </div>
                  <div className="flex items-end">
                    <label className="flex items-center gap-2 text-zinc-300 text-sm cursor-pointer bg-[#0A0A0A] border border-white/10 rounded-lg px-4 py-2.5">
                      <input
                        type="checkbox"
                        checked={newCode.new_users_only}
                        onChange={(e) => setNewCode({...newCode, new_users_only: e.target.checked})}
                        className="rounded border-white/20"
                      />
                      Only for new users
                    </label>
                  </div>
                </div>
                <Button 
                  onClick={createDiscountCode} 
                  disabled={creatingCode || !newCode.code || !newCode.discount_value}
                  className="bg-green-600 hover:bg-green-500"
                  data-testid="create-discount-btn"
                >
                  {creatingCode ? 'Creating...' : 'Create Code'}
                </Button>
              </CardContent>
            </Card>

            {/* Existing Codes */}
            <Card className="bg-[#121212] border-white/10">
              <CardHeader>
                <CardTitle className="text-white">Discount Codes ({discountCodes.length})</CardTitle>
              </CardHeader>
              <CardContent>
                {discountCodes.length === 0 ? (
                  <p className="text-zinc-500 text-center py-8">No discount codes yet</p>
                ) : (
                  <>
                    {/* Mobile Cards - hidden on desktop */}
                    <div className="md:hidden space-y-3">
                      {discountCodes.map(code => {
                        const isExpired = code.expires_at && new Date(code.expires_at) < new Date();
                        const isMaxedOut = code.max_uses && code.used_count >= code.max_uses;
                        return (
                          <div key={code.id} className="rounded-lg border border-white/10 bg-[#1A1A1A] p-3">
                            <div className="flex items-start justify-between gap-3">
                              <div className="min-w-0 flex-1">
                                <div className="flex items-center gap-2 flex-wrap">
                                  <span className="text-white font-mono font-bold">{code.code}</span>
                                  <span className="text-green-400 font-semibold">
                                    {code.discount_type === 'percent' ? `${code.discount_value}%` : `£${code.discount_value}`}
                                  </span>
                                </div>
                                {code.description && <p className="text-zinc-500 text-xs mt-1">{code.description}</p>}
                                <div className="mt-2 flex flex-wrap items-center gap-2">
                                  {isExpired ? (
                                    <Badge className="bg-red-600/20 text-red-400">Expired</Badge>
                                  ) : isMaxedOut ? (
                                    <Badge className="bg-yellow-600/20 text-yellow-400">Maxed Out</Badge>
                                  ) : code.is_active ? (
                                    <Badge className="bg-green-600/20 text-green-400">Active</Badge>
                                  ) : (
                                    <Badge className="bg-zinc-600/20 text-zinc-400">Inactive</Badge>
                                  )}
                                  {code.new_users_only && (
                                    <Badge className="bg-orange-600/20 text-orange-400 text-[10px]">New users only</Badge>
                                  )}
                                  {code.valid_for_tiers?.map(tier => (
                                    <Badge key={tier} variant="outline" className={`text-xs ${
                                      tier === 'pro' ? 'border-purple-500 text-purple-400' : 'border-blue-500 text-blue-400'
                                    }`}>{tier}</Badge>
                                  ))}
                                </div>
                                <div className="mt-2 text-xs text-zinc-500">
                                  Used: {code.used_count || 0}/{code.max_uses || '∞'} · Expires: {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : 'Never'}
                                </div>
                              </div>
                              <div className="shrink-0 flex items-center gap-1">
                                <Button
                                  variant="ghost"
                                  size="sm"
                                  onClick={() => toggleDiscountCode(code.id)}
                                  className={`h-9 w-9 p-0 ${code.is_active ? 'text-yellow-400' : 'text-green-400'}`}
                                >
                                  {code.is_active ? <Ban className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
                                </Button>
                                <Button variant="ghost" size="sm" onClick={() => deleteDiscountCode(code.id)} className="h-9 w-9 p-0">
                                  <Trash2 className="w-4 h-4 text-red-400" />
                                </Button>
                              </div>
                            </div>
                          </div>
                        );
                      })}
                    </div>

                    {/* Desktop Table - hidden on mobile */}
                    <div className="hidden md:block">
                      <Table>
                        <TableHeader>
                          <TableRow className="border-white/10">
                            <TableHead className="text-zinc-400">Code</TableHead>
                            <TableHead className="text-zinc-400">Discount</TableHead>
                            <TableHead className="text-zinc-400">Usage</TableHead>
                            <TableHead className="text-zinc-400">Expires</TableHead>
                            <TableHead className="text-zinc-400">Plans</TableHead>
                            <TableHead className="text-zinc-400">Status</TableHead>
                            <TableHead className="text-zinc-400">Actions</TableHead>
                          </TableRow>
                        </TableHeader>
                        <TableBody>
                          {discountCodes.map(code => {
                            const isExpired = code.expires_at && new Date(code.expires_at) < new Date();
                            const isMaxedOut = code.max_uses && code.used_count >= code.max_uses;
                            return (
                              <TableRow key={code.id} className="border-white/10">
                                <TableCell>
                                  <span className="text-white font-mono font-bold">{code.code}</span>
                                  {code.description && (
                                    <p className="text-zinc-500 text-xs mt-1">{code.description}</p>
                                  )}
                                  {code.new_users_only && (
                                    <Badge className="bg-orange-600/20 text-orange-400 text-[10px] mt-1">New users only</Badge>
                                  )}
                                </TableCell>
                                <TableCell className="text-green-400 font-semibold">
                                  {code.discount_type === 'percent' ? `${code.discount_value}%` : `£${code.discount_value}`}
                                  <span className="text-zinc-500 text-xs block">first month</span>
                                </TableCell>
                                <TableCell className="text-zinc-300">
                                  {code.used_count || 0} / {code.max_uses || '∞'}
                                </TableCell>
                                <TableCell className={isExpired ? 'text-red-400' : 'text-zinc-300'}>
                                  {code.expires_at ? new Date(code.expires_at).toLocaleDateString() : 'Never'}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    {code.valid_for_tiers?.map(tier => (
                                      <Badge key={tier} variant="outline" className={`text-xs ${
                                        tier === 'pro' ? 'border-purple-500 text-purple-400' : 'border-blue-500 text-blue-400'
                                      }`}>{tier}</Badge>
                                    ))}
                                  </div>
                                </TableCell>
                                <TableCell>
                                  {isExpired ? (
                                    <Badge className="bg-red-600/20 text-red-400">Expired</Badge>
                                  ) : isMaxedOut ? (
                                    <Badge className="bg-yellow-600/20 text-yellow-400">Maxed Out</Badge>
                                  ) : code.is_active ? (
                                    <Badge className="bg-green-600/20 text-green-400">Active</Badge>
                                  ) : (
                                    <Badge className="bg-zinc-600/20 text-zinc-400">Inactive</Badge>
                                  )}
                                </TableCell>
                                <TableCell>
                                  <div className="flex gap-1">
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => toggleDiscountCode(code.id)}
                                      className={code.is_active ? 'text-yellow-400 hover:text-yellow-300' : 'text-green-400 hover:text-green-300'}
                                      title={code.is_active ? 'Deactivate' : 'Activate'}
                                    >
                                      {code.is_active ? <Ban className="w-4 h-4" /> : <RefreshCw className="w-4 h-4" />}
                                    </Button>
                                    <Button
                                      variant="ghost"
                                      size="sm"
                                      onClick={() => deleteDiscountCode(code.id)}
                                      className="text-red-400 hover:text-red-300"
                                    >
                                      <Trash2 className="w-4 h-4" />
                                    </Button>
                                  </div>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </div>
                  </>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Pricing Tab */}
        {activeTab === 'pricing' && pricing && (
          <Card className="bg-[#121212] border-white/10 mb-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <CreditCard className="w-5 h-5" /> Pricing Management
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              <p className="text-zinc-400 text-sm">
                Edit pricing tiers below. Changes will be visible on the Pricing page immediately.
              </p>
              
              {/* Core Tier */}
              <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Badge className="bg-blue-600">Core</Badge>
                </h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">Price (GBP)</label>
                    <Input
                      type="number"
                      value={pricing.tiers?.core?.price ?? ''}
                      onChange={(e) => updatePricingTier('core', 'price', parseInt(e.target.value) || 0)}
                      className="bg-[#1A1A1A] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">Discount %</label>
                    <Input
                      type="number"
                      value={pricing.tiers?.core?.discount_percent ?? 0}
                      onChange={(e) => updatePricingTier('core', 'discount_percent', parseInt(e.target.value) || 0)}
                      className="bg-[#1A1A1A] border-white/10 text-white"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={pricing.tiers?.core?.special_offer || false}
                      onChange={(e) => updatePricingTier('core', 'special_offer', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label className="text-zinc-300 text-sm">Special Offer</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={pricing.tiers?.core?.popular || false}
                      onChange={(e) => updatePricingTier('core', 'popular', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label className="text-zinc-300 text-sm">Most Popular</label>
                  </div>
                  {pricing.tiers?.core?.special_offer && (
                    <Input
                      placeholder="e.g., Limited Time!"
                      value={pricing.tiers?.core?.special_offer_text || ''}
                      onChange={(e) => updatePricingTier('core', 'special_offer_text', e.target.value)}
                      className="bg-[#1A1A1A] border-white/10 text-white flex-1"
                    />
                  )}
                </div>

                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Features</label>
                  {pricing.tiers?.core?.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <Input
                        value={feature}
                        onChange={(e) => updatePricingFeature('core', idx, e.target.value)}
                        className="bg-[#1A1A1A] border-white/10 text-white flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removePricingFeature('core', idx)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addPricingFeature('core')}
                    className="border-white/20 text-zinc-400 mt-2"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Feature
                  </Button>
                </div>
              </div>

              {/* Pro Tier */}
              <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Badge className="bg-purple-600">Pro</Badge>
                </h3>
                
                <div className="grid grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">Price (GBP)</label>
                    <Input
                      type="number"
                      value={pricing.tiers?.pro?.price ?? ''}
                      onChange={(e) => updatePricingTier('pro', 'price', parseInt(e.target.value) || 0)}
                      className="bg-[#1A1A1A] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">Discount %</label>
                    <Input
                      type="number"
                      value={pricing.tiers?.pro?.discount_percent ?? 0}
                      onChange={(e) => updatePricingTier('pro', 'discount_percent', parseInt(e.target.value) || 0)}
                      className="bg-[#1A1A1A] border-white/10 text-white"
                      min="0"
                      max="100"
                    />
                  </div>
                </div>

                <div className="flex items-center gap-4 mb-4">
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={pricing.tiers?.pro?.special_offer || false}
                      onChange={(e) => updatePricingTier('pro', 'special_offer', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label className="text-zinc-300 text-sm">Special Offer</label>
                  </div>
                  <div className="flex items-center gap-2">
                    <input
                      type="checkbox"
                      checked={pricing.tiers?.pro?.popular || false}
                      onChange={(e) => updatePricingTier('pro', 'popular', e.target.checked)}
                      className="w-4 h-4"
                    />
                    <label className="text-zinc-300 text-sm">Most Popular</label>
                  </div>
                  {pricing.tiers?.pro?.special_offer && (
                    <Input
                      placeholder="e.g., Best Value!"
                      value={pricing.tiers?.pro?.special_offer_text || ''}
                      onChange={(e) => updatePricingTier('pro', 'special_offer_text', e.target.value)}
                      className="bg-[#1A1A1A] border-white/10 text-white flex-1"
                    />
                  )}
                </div>

                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Features</label>
                  {pricing.tiers?.pro?.features?.map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <Input
                        value={feature}
                        onChange={(e) => updatePricingFeature('pro', idx, e.target.value)}
                        className="bg-[#1A1A1A] border-white/10 text-white flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removePricingFeature('pro', idx)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addPricingFeature('pro')}
                    className="border-white/20 text-zinc-400 mt-2"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Feature
                  </Button>
                </div>
              </div>

              {/* Enterprise Tier */}
              <div className="p-4 bg-[#0D0D0D] rounded-lg border border-purple-500/30">
                <h3 className="text-lg font-semibold text-white mb-4 flex items-center gap-2">
                  <Badge className="bg-purple-600">Enterprise</Badge>
                  <span className="text-zinc-500 text-xs font-normal ml-2">Custom pricing per user</span>
                </h3>
                
                <p className="text-zinc-400 text-sm mb-4">
                  Enterprise pricing is set individually when upgrading each user. Edit the description and features shown on the Pricing page below.
                </p>

                <div className="mb-4">
                  <label className="text-zinc-400 text-sm mb-1 block">Description</label>
                  <Input
                    value={pricing.tiers?.enterprise?.description ?? 'For agencies & developers with custom requirements'}
                    onChange={(e) => updatePricingTier('enterprise', 'description', e.target.value)}
                    className="bg-[#1A1A1A] border-white/10 text-white"
                    placeholder="For agencies & developers with custom requirements"
                  />
                </div>

                <div>
                  <label className="text-zinc-400 text-sm mb-2 block">Features</label>
                  {(pricing.tiers?.enterprise?.features || []).map((feature, idx) => (
                    <div key={idx} className="flex items-center gap-2 mb-2">
                      <Input
                        value={feature}
                        onChange={(e) => updatePricingFeature('enterprise', idx, e.target.value)}
                        className="bg-[#1A1A1A] border-white/10 text-white flex-1"
                      />
                      <Button
                        variant="ghost"
                        size="sm"
                        onClick={() => removePricingFeature('enterprise', idx)}
                        className="text-red-400 hover:text-red-300"
                      >
                        <Trash2 className="w-4 h-4" />
                      </Button>
                    </div>
                  ))}
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => addPricingFeature('enterprise')}
                    className="border-white/20 text-zinc-400 mt-2"
                  >
                    <Plus className="w-4 h-4 mr-1" /> Add Feature
                  </Button>
                </div>
              </div>

              <div className="flex gap-4">
                <Button
                  onClick={savePricing}
                  disabled={savingPricing}
                  className="flex-1 bg-blue-600 hover:bg-blue-500"
                >
                  {savingPricing ? 'Saving...' : 'Save Pricing Changes'}
                </Button>
                <Button
                  variant="outline"
                  onClick={() => setPricing(defaultPricing)}
                  className="border-orange-500/50 text-orange-400 hover:bg-orange-500/10"
                >
                  Reset to Defaults
                </Button>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Settings Tab */}
        {activeTab === 'settings' && (
          <Card className="bg-[#121212] border-white/10 mb-8">
            <CardHeader>
              <CardTitle className="text-white flex items-center gap-2">
                <Settings className="w-5 h-5" /> Platform Settings
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Payments Toggle */}
              <div className="flex items-center justify-between p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings.payments_enabled ? 'bg-green-500/20' : 'bg-red-500/20'}`}>
                    <CreditCard className={`w-5 h-5 ${settings.payments_enabled ? 'text-green-400' : 'text-red-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Accept Payments</h3>
                    <p className="text-zinc-500 text-sm">Enable or disable new subscription purchases</p>
                  </div>
                </div>
                <Switch
                  checked={settings.payments_enabled}
                  onCheckedChange={handleTogglePayments}
                />
              </div>

              {/* Maintenance Mode Toggle */}
              <div className="flex items-center justify-between p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                <div className="flex items-center gap-4">
                  <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${settings.maintenance_mode ? 'bg-yellow-500/20' : 'bg-zinc-500/20'}`}>
                    <AlertTriangle className={`w-5 h-5 ${settings.maintenance_mode ? 'text-yellow-400' : 'text-zinc-400'}`} />
                  </div>
                  <div>
                    <h3 className="text-white font-medium">Maintenance Mode</h3>
                    <p className="text-zinc-500 text-sm">Show maintenance message to users</p>
                  </div>
                </div>
                <Switch
                  checked={settings.maintenance_mode}
                  onCheckedChange={handleToggleMaintenance}
                />
              </div>

              {/* Maintenance Message */}
              {settings.maintenance_mode && (
                <div className="p-4 bg-[#0D0D0D] rounded-lg border border-yellow-500/30">
                  <label className="text-zinc-400 text-sm mb-2 block">Custom Message (optional)</label>
                  <Input
                    placeholder="Leave empty for default message"
                    value={settings.maintenance_message || ''}
                    onChange={(e) => setSettings({ ...settings, maintenance_message: e.target.value })}
                    className="bg-[#1A1A1A] border-white/10 text-white mb-2"
                  />
                  <Button
                    size="sm"
                    onClick={async () => {
                      try {
                        await axios.post(`${API}/admin/settings`, settings);
                        toast.success('Maintenance message saved');
                      } catch (err) {
                        toast.error('Failed to save message');
                      }
                    }}
                    className="bg-yellow-600 hover:bg-yellow-500"
                  >
                    Save Message
                  </Button>
                  <p className="text-zinc-600 text-xs mt-2">
                    Default: "To ensure the highest quality of service, our algorithms are being updated..."
                  </p>
                </div>
              )}

              {/* Landing Page Popup Announcement */}
              <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                <div className="flex items-center justify-between mb-4">
                  <div className="flex items-center gap-4">
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${popupConfig.enabled ? 'bg-blue-500/20' : 'bg-zinc-500/20'}`}>
                      <MessageSquare className={`w-5 h-5 ${popupConfig.enabled ? 'text-blue-400' : 'text-zinc-400'}`} />
                    </div>
                    <div>
                      <h3 className="text-white font-medium">Landing Page Popup</h3>
                      <p className="text-zinc-500 text-sm">Show announcement popup for non-logged-in visitors</p>
                    </div>
                  </div>
                  <Switch
                    data-testid="popup-toggle"
                    checked={popupConfig.enabled}
                    onCheckedChange={(val) => setPopupConfig({ ...popupConfig, enabled: val })}
                  />
                </div>

                <div className="space-y-3 mt-4">
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">Title</label>
                    <Input
                      data-testid="popup-title-input"
                      placeholder="Announcement title..."
                      value={popupConfig.title}
                      onChange={(e) => setPopupConfig({ ...popupConfig, title: e.target.value })}
                      className="bg-[#1A1A1A] border-white/10 text-white"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">Text</label>
                    <textarea
                      data-testid="popup-text-input"
                      placeholder="Announcement body text..."
                      value={popupConfig.text}
                      onChange={(e) => setPopupConfig({ ...popupConfig, text: e.target.value })}
                      rows={4}
                      className="w-full bg-[#1A1A1A] border border-white/10 text-white rounded-md px-3 py-2 text-sm resize-y focus:outline-none focus:ring-1 focus:ring-blue-500"
                    />
                  </div>
                  <div>
                    <label className="text-zinc-400 text-sm mb-1 block">Background Color</label>
                    <div className="flex items-center gap-3">
                      <input
                        data-testid="popup-color-input"
                        type="color"
                        value={popupConfig.bg_color}
                        onChange={(e) => setPopupConfig({ ...popupConfig, bg_color: e.target.value })}
                        className="w-10 h-10 rounded cursor-pointer border border-white/10 bg-transparent"
                      />
                      <Input
                        value={popupConfig.bg_color}
                        onChange={(e) => setPopupConfig({ ...popupConfig, bg_color: e.target.value })}
                        className="bg-[#1A1A1A] border-white/10 text-white w-32"
                        placeholder="#1a1a2e"
                      />
                      <div className="flex gap-1">
                        {['#1a1a2e', '#0f172a', '#1e1b4b', '#1c1917', '#052e16'].map(c => (
                          <button key={c} onClick={() => setPopupConfig({ ...popupConfig, bg_color: c })} className="w-6 h-6 rounded border border-white/10 hover:scale-110 transition-transform" style={{ backgroundColor: c }} />
                        ))}
                      </div>
                    </div>
                  </div>
                  <div className="grid grid-cols-2 gap-3">
                    <div>
                      <label className="text-zinc-400 text-sm mb-1 block">CTA Button Text (optional)</label>
                      <Input
                        data-testid="popup-cta-text-input"
                        placeholder="e.g. Learn more"
                        value={popupConfig.cta_text}
                        onChange={(e) => setPopupConfig({ ...popupConfig, cta_text: e.target.value })}
                        className="bg-[#1A1A1A] border-white/10 text-white"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-sm mb-1 block">CTA Link</label>
                      <Input
                        data-testid="popup-cta-link-input"
                        placeholder="e.g. /pricing or https://..."
                        value={popupConfig.cta_link}
                        onChange={(e) => setPopupConfig({ ...popupConfig, cta_link: e.target.value })}
                        className="bg-[#1A1A1A] border-white/10 text-white"
                      />
                    </div>
                  </div>

                  {/* Preview */}
                  {(popupConfig.title || popupConfig.text) && (
                    <div className="mt-3 p-4 rounded-lg border border-white/10" style={{ backgroundColor: popupConfig.bg_color }}>
                      <p className="text-zinc-500 text-xs mb-2 uppercase tracking-wider">Preview</p>
                      {popupConfig.title && <h4 className="text-white font-bold text-lg mb-1">{popupConfig.title}</h4>}
                      {popupConfig.text && <p className="text-zinc-300 text-sm whitespace-pre-wrap">{popupConfig.text}</p>}
                      {popupConfig.cta_text && (
                        <button className="mt-3 bg-blue-600 text-white text-sm font-medium px-4 py-2 rounded-sm">{popupConfig.cta_text}</button>
                      )}
                    </div>
                  )}

                  <Button
                    data-testid="popup-save-btn"
                    disabled={savingPopup}
                    onClick={async () => {
                      setSavingPopup(true);
                      try {
                        await axios.put(`${API}/admin/popup-config`, popupConfig);
                        toast.success('Popup config saved');
                      } catch (err) {
                        toast.error('Failed to save popup config');
                      } finally {
                        setSavingPopup(false);
                      }
                    }}
                    className="bg-blue-600 hover:bg-blue-500 w-full"
                  >
                    {savingPopup ? 'Saving...' : 'Save Popup Config'}
                  </Button>
                </div>
              </div>

              {/* Status indicators */}
              <div className="grid grid-cols-2 gap-4 mt-6">
                <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                  <p className="text-zinc-500 text-sm mb-1">Payments Status</p>
                  <Badge className={settings.payments_enabled ? 'bg-green-600' : 'bg-red-600'}>
                    {settings.payments_enabled ? 'Enabled' : 'Disabled'}
                  </Badge>
                </div>
                <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                  <p className="text-zinc-500 text-sm mb-1">Maintenance Mode</p>
                  <Badge className={settings.maintenance_mode ? 'bg-yellow-600' : 'bg-zinc-600'}>
                    {settings.maintenance_mode ? 'ON' : 'OFF'}
                  </Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        )}

        {/* Upgrade User Modal */}
        {upgradeUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setUpgradeUser(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <ArrowUp className="w-5 h-5 text-blue-400" /> Change Subscription
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                  <p className="text-zinc-500 text-sm">User</p>
                  <p className="text-white font-medium">{upgradeUser.email}</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Current plan: <Badge className={
                      upgradeUser.tier === 'pro' ? 'bg-purple-600' :
                      upgradeUser.tier === 'entry' ? 'bg-blue-600' : 'bg-zinc-600'
                    }>{upgradeUser.tier || 'Free'}</Badge>
                  </p>
                </div>

                <div>
                  <p className="text-zinc-400 text-sm mb-2">Select new tier:</p>
                  <Select value={upgradeTier} onValueChange={setUpgradeTier}>
                    <SelectTrigger className="bg-[#0D0D0D] border-white/10 text-white">
                      <SelectValue placeholder="Select tier" />
                    </SelectTrigger>
                    <SelectContent className="bg-[#1A1A1A] border-white/10">
                      <SelectItem value="free" className="text-white">Free (0 credits)</SelectItem>
                      <SelectItem value="core" className="text-white">Core (30 credits/mo)</SelectItem>
                      <SelectItem value="pro" className="text-white">Pro (90 credits/mo)</SelectItem>
                      <SelectItem value="enterprise" className="text-purple-400">Enterprise (Custom)</SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Enterprise Custom Configuration */}
                {upgradeTier === 'enterprise' && (
                  <div className="space-y-3 p-4 bg-purple-500/10 border border-purple-500/20 rounded-lg">
                    <p className="text-purple-400 text-sm font-medium">Enterprise Custom Configuration</p>
                    <div>
                      <label className="text-zinc-400 text-xs">Custom Price (£/month)</label>
                      <Input
                        type="number"
                        min="0"
                        placeholder="e.g. 199"
                        value={enterpriseConfig.custom_price}
                        onChange={(e) => setEnterpiseConfig({...enterpriseConfig, custom_price: e.target.value})}
                        className="bg-[#0D0D0D] border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs">Custom Credits (per month)</label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="e.g. 50"
                        value={enterpriseConfig.custom_credits}
                        onChange={(e) => setEnterpiseConfig({...enterpriseConfig, custom_credits: e.target.value})}
                        className="bg-[#0D0D0D] border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs">Custom Domains Limit</label>
                      <Input
                        type="number"
                        min="1"
                        placeholder="e.g. 10 (leave empty for unlimited)"
                        value={enterpriseConfig.custom_domains}
                        onChange={(e) => setEnterpiseConfig({...enterpriseConfig, custom_domains: e.target.value})}
                        className="bg-[#0D0D0D] border-white/10 text-white mt-1"
                      />
                    </div>
                    <div>
                      <label className="text-zinc-400 text-xs">First Month Discount (%)</label>
                      <Input
                        type="number"
                        min="0"
                        max="100"
                        placeholder="e.g. 25 for 25% off first month"
                        value={enterpriseConfig.first_month_discount}
                        onChange={(e) => setEnterpiseConfig({...enterpriseConfig, first_month_discount: e.target.value})}
                        className="bg-[#0D0D0D] border-white/10 text-white mt-1"
                      />
                      {enterpriseConfig.first_month_discount && enterpriseConfig.custom_price && (
                        <p className="text-green-400 text-xs mt-1">
                          First month: £{Math.round(enterpriseConfig.custom_price * (1 - enterpriseConfig.first_month_discount / 100))}, then £{enterpriseConfig.custom_price}/mo
                        </p>
                      )}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setUpgradeUser(null)}
                    className="flex-1 border-white/20 text-white"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleUpgradeUser}
                    className={`flex-1 ${upgradeTier === 'enterprise' ? 'bg-purple-600 hover:bg-purple-500' : 'bg-blue-600 hover:bg-blue-500'}`}
                  >
                    <ArrowUp className="w-4 h-4 mr-2" /> Update Tier
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Add Credits Modal */}
        {addCreditsUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setAddCreditsUser(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Plus className="w-5 h-5 text-green-400" /> Modify Credits
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                  <p className="text-zinc-500 text-sm">User</p>
                  <p className="text-white font-medium">{addCreditsUser.email}</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Current credits: <span className="text-white font-bold">{addCreditsUser.credits_remaining || 0}</span>
                  </p>
                </div>

                <div>
                  <p className="text-zinc-400 text-sm mb-2">Credits to add/subtract (use negative to subtract):</p>
                  <Input
                    type="number"
                    min="-100"
                    max="100"
                    value={creditsToAdd}
                    onChange={(e) => setCreditsToAdd(parseInt(e.target.value) || 0)}
                    className="bg-[#0D0D0D] border-white/10 text-white"
                  />
                  {creditsToAdd < 0 && (
                    <p className="text-yellow-400 text-xs mt-2">⚠️ This will subtract {Math.abs(creditsToAdd)} credits from user's balance</p>
                  )}
                </div>

                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setAddCreditsUser(null)}
                    className="flex-1 border-white/20 text-white"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleAddCredits}
                    disabled={creditsToAdd === 0}
                    className={`flex-1 ${creditsToAdd < 0 ? 'bg-red-600 hover:bg-red-500' : 'bg-green-600 hover:bg-green-500'}`}
                  >
                    {creditsToAdd < 0 ? (
                      <>Subtract {Math.abs(creditsToAdd)} Credits</>
                    ) : (
                      <><Plus className="w-4 h-4 mr-2" /> Add {creditsToAdd} Credits</>
                    )}
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Reset Password Modal */}
        {resetPasswordUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setResetPasswordUser(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Key className="w-5 h-5 text-yellow-400" /> Reset Password
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                  <p className="text-zinc-500 text-sm">User</p>
                  <p className="text-white font-medium">{resetPasswordUser.email}</p>
                  {resetPasswordUser.google_id && (
                    <p className="text-yellow-400 text-xs mt-2">⚠️ This user uses Google login</p>
                  )}
                </div>

                <div>
                  <p className="text-zinc-400 text-sm mb-2">New password:</p>
                  <Input
                    type="text"
                    value={newPassword}
                    onChange={(e) => setNewPassword(e.target.value)}
                    placeholder="Enter new password (min 6 chars)"
                    className="bg-[#0D0D0D] border-white/10 text-white"
                  />
                </div>

                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setResetPasswordUser(null); setNewPassword(''); }}
                    className="flex-1 border-white/20 text-white"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleResetPassword}
                    disabled={newPassword.length < 6}
                    className="flex-1 bg-yellow-600 hover:bg-yellow-500"
                  >
                    <Key className="w-4 h-4 mr-2" /> Reset Password
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Block User Modal */}
        {blockUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setBlockUser(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <Ban className="w-5 h-5 text-orange-400" /> 
                  {blockUser.is_blocked ? 'Unblock User' : 'Block User'}
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                  <p className="text-zinc-500 text-sm">User</p>
                  <p className="text-white font-medium">{blockUser.email}</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Status: <Badge className={blockUser.is_blocked ? 'bg-red-600' : 'bg-green-600'}>
                      {blockUser.is_blocked ? 'Blocked' : 'Active'}
                    </Badge>
                  </p>
                </div>

                {!blockUser.is_blocked && (
                  <div>
                    <p className="text-zinc-400 text-sm mb-2">Block reason (optional):</p>
                    <Input
                      type="text"
                      value={blockReason}
                      onChange={(e) => setBlockReason(e.target.value)}
                      placeholder="e.g., Spam, Abuse, Non-payment"
                      className="bg-[#0D0D0D] border-white/10 text-white"
                    />
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setBlockUser(null); setBlockReason(''); }}
                    className="flex-1 border-white/20 text-white"
                  >
                    Cancel
                  </Button>
                  {blockUser.is_blocked ? (
                    <Button 
                      onClick={() => handleBlockUser(false)}
                      className="flex-1 bg-green-600 hover:bg-green-500"
                    >
                      Unblock User
                    </Button>
                  ) : (
                    <Button 
                      onClick={() => handleBlockUser(true)}
                      className="flex-1 bg-red-600 hover:bg-red-500"
                    >
                      <Ban className="w-4 h-4 mr-2" /> Block User
                    </Button>
                  )}
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Set Stripe Customer ID Modal */}
        {stripeCustomerUser && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => setStripeCustomerUser(null)}>
            <Card className="bg-[#121212] border-white/10 max-w-md w-full" onClick={e => e.stopPropagation()}>
              <CardHeader>
                <CardTitle className="text-white flex items-center gap-2">
                  <CreditCard className="w-5 h-5 text-blue-400" /> 
                  Set Stripe Customer ID
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="p-4 bg-[#0D0D0D] rounded-lg border border-white/10">
                  <p className="text-zinc-500 text-sm">User</p>
                  <p className="text-white font-medium">{stripeCustomerUser.email}</p>
                  <p className="text-zinc-400 text-sm mt-1">
                    Current: {stripeCustomerUser.customer_id || stripeCustomerUser.subscription?.customer_id ? (
                      <span className="text-green-400 font-mono text-xs">{stripeCustomerUser.customer_id || stripeCustomerUser.subscription?.customer_id}</span>
                    ) : (
                      <span className="text-red-400">Not set</span>
                    )}
                  </p>
                </div>

                <div>
                  <p className="text-zinc-400 text-sm mb-2">Stripe Customer ID:</p>
                  <Input
                    type="text"
                    value={stripeCustomerId}
                    onChange={(e) => setStripeCustomerId(e.target.value)}
                    placeholder="cus_xxxxxxxxxxxxx"
                    className="bg-[#0D0D0D] border-white/10 text-white"
                  />
                  <p className="text-zinc-500 text-xs mt-2">
                    Find in Stripe Dashboard → Customers → click user → copy ID
                  </p>
                </div>

                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => { setStripeCustomerUser(null); setStripeCustomerId(''); }}
                    className="flex-1 border-white/20 text-white"
                  >
                    Cancel
                  </Button>
                  <Button 
                    onClick={handleSetStripeCustomer}
                    className="flex-1 bg-blue-600 hover:bg-blue-500"
                    disabled={!stripeCustomerId.startsWith('cus_')}
                  >
                    Save
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Click Details Modal */}
        <Dialog open={!!clickDetailsAudit} onOpenChange={(open) => !open && setClickDetailsAudit(null)}>
          <DialogContent className="bg-[#1A1A1A] border-white/10 max-w-md">
            <DialogHeader>
              <DialogTitle className="text-white flex items-center gap-2">
                <Check className="w-5 h-5 text-green-400" />
                Link Click Details
              </DialogTitle>
            </DialogHeader>
            {clickDetailsAudit && (
              <div className="space-y-4">
                <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                  <p className="text-xs text-zinc-500 mb-1">Domain</p>
                  <p className="text-sm text-white font-medium break-all">
                    {clickDetailsAudit.target_url || clickDetailsAudit.report?.request?.target?.normalized_domain || '-'}
                  </p>
                </div>
                
                <div className="grid grid-cols-1 gap-3">
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Clock className="w-3.5 h-3.5 text-zinc-500" />
                      <p className="text-xs text-zinc-500">Clicked at</p>
                    </div>
                    <p className="text-sm text-white">
                      {clickDetailsAudit.link_clicked_at 
                        ? new Date(clickDetailsAudit.link_clicked_at).toLocaleString('pl-PL', {
                            dateStyle: 'medium',
                            timeStyle: 'medium'
                          })
                        : '-'}
                    </p>
                  </div>
                  
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Globe className="w-3.5 h-3.5 text-zinc-500" />
                      <p className="text-xs text-zinc-500">IP Address</p>
                    </div>
                    <a 
                      href={`https://whatismyipaddress.com/ip/${clickDetailsAudit.link_clicked_ip}`}
                      target="_blank" rel="noopener noreferrer"
                      className="text-sm text-blue-400 hover:text-blue-300 font-mono underline"
                    >
                      {clickDetailsAudit.link_clicked_ip || 'Not recorded'}
                    </a>
                    {clickDetailsAudit.link_clicked_geo?.city && (
                      <p className="text-xs text-zinc-400 mt-1">
                        {clickDetailsAudit.link_clicked_geo.city}
                        {clickDetailsAudit.link_clicked_geo.region && clickDetailsAudit.link_clicked_geo.region !== clickDetailsAudit.link_clicked_geo.city ? `, ${clickDetailsAudit.link_clicked_geo.region}` : ''}
                        {clickDetailsAudit.link_clicked_geo.country ? ` — ${clickDetailsAudit.link_clicked_geo.country}` : ''}
                      </p>
                    )}
                  </div>
                  
                  <div className="p-3 rounded-lg bg-white/5 border border-white/10">
                    <div className="flex items-center gap-2 mb-1">
                      <Monitor className="w-3.5 h-3.5 text-zinc-500" />
                      <p className="text-xs text-zinc-500">User Agent</p>
                    </div>
                    <p className="text-xs text-zinc-300 break-all">
                      {clickDetailsAudit.link_clicked_user_agent || 'Not recorded'}
                    </p>
                  </div>
                </div>

                {clickDetailsAudit.link_clicks && clickDetailsAudit.link_clicks.length > 1 && (
                  <div className="p-3 rounded-lg bg-blue-500/10 border border-blue-500/20">
                    <p className="text-xs text-blue-400 mb-2">
                      Total clicks: {clickDetailsAudit.link_clicks.length}
                    </p>
                    <div className="max-h-40 overflow-y-auto space-y-1.5">
                      {clickDetailsAudit.link_clicks.map((click, i) => (
                        <div key={i} className="flex items-center gap-1.5 text-xs text-zinc-400">
                          <span className="shrink-0">{new Date(click.clicked_at).toLocaleString('pl-PL')}</span>
                          <span>—</span>
                          <a 
                            href={`https://whatismyipaddress.com/ip/${click.ip}`}
                            target="_blank" rel="noopener noreferrer"
                            className="text-blue-400 hover:text-blue-300 underline font-mono shrink-0"
                          >
                            {click.ip}
                          </a>
                          {click.geo?.city && (
                            <span className="text-zinc-500 truncate">({click.geo.city}, {click.geo.countryCode || click.geo.country})</span>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                <div className="flex gap-3 pt-2">
                  <Button 
                    variant="outline" 
                    onClick={() => setClickDetailsAudit(null)}
                    className="flex-1 border-white/20 text-white"
                  >
                    Close
                  </Button>
                  <Button 
                    onClick={() => {
                      handleCopyAuditLink(clickDetailsAudit, true);
                      setClickDetailsAudit(null);
                    }}
                    className="flex-1 bg-blue-600 hover:bg-blue-500"
                  >
                    <Copy className="w-4 h-4 mr-2" />
                    Copy link again
                  </Button>
                </div>
              </div>
            )}
          </DialogContent>
        </Dialog>

        </div>
      </div>

      {/* Delete Single Audit Confirmation */}
      <AlertDialog open={!!deleteAuditTarget} onOpenChange={(open) => !open && setDeleteAuditTarget(null)}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete Audit</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to delete the audit for <span className="text-white font-medium">{deleteAuditTarget?.target_url || deleteAuditTarget?.domain}</span>? This action cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDeleteAudit} disabled={deletingAudit} className="bg-red-600 hover:bg-red-500 text-white">
              {deletingAudit ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Bulk Delete Confirmation */}
      <AlertDialog open={bulkDeleteOpen} onOpenChange={setBulkDeleteOpen}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Bulk Delete Audits</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Delete {selectedAuditIds.length} selected audits? Locked audits will be skipped. This cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmBulkDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Filter Delete Confirmation */}
      <AlertDialog open={filterDeleteOpen} onOpenChange={setFilterDeleteOpen}>
        <AlertDialogContent className="bg-[#121212] border-white/10">
          <AlertDialogHeader>
            <AlertDialogTitle className="text-white">Delete All Filtered Audits</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Delete ALL {auditsTotal} audits matching current filters? Locked audits will be skipped. This is permanent and cannot be undone!
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmFilterDelete} className="bg-red-600 hover:bg-red-500 text-white">Delete All</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

    </AppLayout>
  );
}
